 /**
 * ClientFirstDocumentEditor - Main component integrating all Client-First systems
 * 
 * The complete Word-class legal document editor with immediate responsiveness,
 * real-time collaboration, legal compliance, and professional output.
 */

 import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';

 // Core systems
 import { LocalDocumentEngine } from './core/LocalDocumentEngine';
 
 // Version Modal
 import { DocumentVersioningModal } from '../../components/DocumentVersioningModal';
 
 // Change Tracking
 import { DocumentChangeButton } from '../../components/DocumentChangeButton';
 
 // Document API for checkout
 import { useDocumentAPI } from '../../DocumentAPI';
 
 // Checkout status for save routing
 import { useWebSocketCheckout } from '../../hooks/useWebSocketCheckout';
 
 // Rendering systems
 // CanvasRenderer disabled - conflicts with active rendering pipeline
 
 // Components
 import { PaperCanvas, ChangeMetadata } from './components/PaperCanvas';
 import { PageSetupDialog } from './components/PageSetupDialog';
 import { TextFormattingIntegration } from './integration/TextFormattingIntegration';
 
// Input systems - Using InputManager with SimplifiedCaretSystem
import { InputManager } from './input/InputManager';
 
 // Synchronization systems
 import { SyncManager } from './sync/SyncManager';
 
 // Integration with existing collaboration system
 import { CollaborationIntegrationBridge } from './integration/CollaborationIntegrationBridge';
 
 // Legal systems
 import { LegalComplianceEngine } from './legal/LegalComplianceEngine';
 import { DocumentImportExportManager } from './legal/DocumentImportExportManager';
 import { DocumentTemplateEngine } from './legal/DocumentTemplateEngine';
 
 // Performance systems
 import { LargeDocumentOptimizer } from './performance/LargeDocumentOptimizer';
 
 // Services
 import { DocumentLoadingService } from './services/DocumentLoadingService';
 import { DMSDocumentService } from './services/DMSDocumentService';
 // import { AutoSaveManager } from './services/AutoSaveManager'; // Commented out - using manual save only
 import { DocumentLifecycleManager } from './services/DocumentLifecycleManager';
 import { TextToASTConverter } from './services/TextToASTConverter';
 import { DPIPersistenceService } from './services/DPIPersistenceService';
 import { ConnectivityManager } from './services/ConnectivityManager';
 import { SaveRoutingManager } from './services/SaveRoutingManager';
 
 // Hit testing for accurate caret positioning
 import { HitTestingEngine } from './components/HitTestingEngine';
 import { CoordinateSpace } from './components/CoordinateSpaces';
 
 // Font Management
 import { FontManager } from './fonts/FontManager';
 import { FontLoadingState, LegalDocumentFontRequirements, LEGAL_FONT_STANDARDS } from './fonts/FontLoadingTypes';
 
 // Phase 1: Backend Service Integration
 // REMOVED: Frontend HarfBuzz client - Use backend-only architecture
 // import { createHarfBuzzClient } from './services/HarfBuzzMicroserviceClient';
 import { createRedisCacheService } from './services/RedisCacheService';
 import { BackendFontService } from './services/BackendFontService';
 
 // Phase 1: React Hooks for State Management
 import {
   useDocumentEngineState, 
   useHarfBuzzMetrics, 
   useDocumentEnginePerformance, 
   useBackendServiceStatus 
 } from './hooks/useDocumentEngineState';
 // FIXED: Using properly optimized hooks that avoid React error #311
 import {
   useDocumentEngineStateFixed as useDocumentEngineStateOptimized,
   useHarfBuzzMetricsFixed as useHarfBuzzMetricsOptimized,
   useDocumentEnginePerformanceFixed as useDocumentEnginePerformanceOptimized,
   useBackendServiceStatusFixed as useBackendServiceStatusOptimized,
   useCacheMetricsFixed as useCacheMetricsOptimized
 } from './hooks/useDocumentEngineStateFixed';
 
 // Performance Metrics Panel
 // Removed: HarfBuzzMetricsPanel moved to Analytics Dashboard for realm-wide visibility
 
 import { LocalDocumentState, AttorneyInfo, Operation, LegalDocumentType, Jurisdiction, TextRange } from './types/DocumentTypes';
 
 // ============================================================================
 // UTILITY FUNCTIONS
 // ============================================================================
 
 /**
  * Debounce utility function
  */
 function debounce<T extends (...args: any[]) => any>(
   func: T,
   delay: number
 ): (...args: Parameters<T>) => void {
   let timeoutId: NodeJS.Timeout;
   return (...args: Parameters<T>) => {
     clearTimeout(timeoutId);
     timeoutId = setTimeout(() => func(...args), delay);
   };
 }
 
 interface ClientFirstDocumentEditorProps {
   documentId?: string; // Optional for new documents
   versionId?: string; // Version-specific editing
   attorney: AttorneyInfo;
   websocketUrl: string;
   auth: any;
   initialContent?: string;
   jurisdiction?: Jurisdiction;
   documentType?: LegalDocumentType;
   onDocumentChange?: (state: LocalDocumentState) => void;
   onCollaboratorJoin?: (collaborator: any) => void;
   onError?: (error: Error) => void;
   
   // DMS Integration props
   dmsBaseUrl?: string;
   authToken?: string;
   clientId?: string;
   matterId?: string;
   documentTitle?: string;
   enableAutoSave?: boolean;
   autoSaveInterval?: number;
   onDocumentSaved?: (documentId: string) => void;
   onVersionCreated?: (versionId: string) => void;
 }
 
 // Enhanced performance metrics interface for HarfBuzz/MSDF preparation
 interface EnhancedPerformanceMetrics {
   // Existing metrics
   renderTime: number;
   frameRate: number;
   
   // Typography-specific metrics (ready for HarfBuzz/MSDF)
   typographyMetrics: {
     averageShapingTime: number;
     averageAtlasLookupTime: number;
     averageRenderingTime: number;
     averageGlyphGenerationTime: number;
     cacheHitRate: number;
     fallbackUsageRate: number;
     totalOperations: number;
     isOptimal: boolean;
   };
   
   // Quality metrics
   qualityMetrics: {
     textRenderingQuality: 'basic' | 'high' | 'ultra'; // Will be 'ultra' with MSDF
     scalingArtifacts: number;
     subpixelAccuracy: number;
   };
   
   // System capabilities
   systemCapabilities: {
     hasHarfBuzz: boolean;
     hasMSDF: boolean;
     hasWebGL: boolean;
     supportedScripts: string[];
   };
 }
 
 interface EditorState {
   isInitialized: boolean;
   isConnected: boolean;
   isLoading: boolean;
   collaborators: any[];
   currentOperation: Operation | null;
   lastSyncTime: Date | null;
   performanceMetrics: EnhancedPerformanceMetrics | null;
   legalAnalysis: any;
   fontLoadingState: FontLoadingState;
   currentFontRequirements: LegalDocumentFontRequirements | null;
 }
 
 export const ClientFirstDocumentEditor: React.FC<ClientFirstDocumentEditorProps> = ({
   documentId,
   versionId,
   attorney,
   websocketUrl,
   auth,
   initialContent = '',
   jurisdiction = 'federal',
   documentType = 'document',
   onDocumentChange,
   onCollaboratorJoin,
   onError,
   
   // DMS Integration props
   dmsBaseUrl,
   authToken,
   clientId,
   matterId,
   documentTitle,
   enableAutoSave = true,
   autoSaveInterval = 30000,
   onDocumentSaved,
   onVersionCreated
 }) => {
   // CRITICAL DEBUG: Track version-specific rendering
   
   // CRITICAL: Log if versionId is missing
   if (!versionId) {
     console.error('❌ [ClientFirstDocumentEditor] VERSION ID IS MISSING! This will cause version isolation to fail!');
     console.error('❌ [ClientFirstDocumentEditor] All props received:', { documentId, versionId, attorney, websocketUrl, auth, jurisdiction, documentType });
   }
   
  // Refs for DOM elements
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const stateSyncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isClickOnlyOperation = useRef<boolean>(false);
   
   // Core system instances
   const [documentEngine] = useState(() => {
     return new LocalDocumentEngine();
   });
   // CanvasRenderer functionality now integrated into enhanced PaperCanvas
   
   // Font Management System
   const [fontManager] = useState(() => {
     return new FontManager({
       supportedFormats: ['woff2', 'woff', 'ttf', 'otf'],
       harfbuzzFeatures: ['kern', 'liga', 'clig', 'smcp', 'c2sc', 'onum', 'tnum'],
       preloadFonts: [
         'Times New Roman',
         'Arial',
         'Courier New',
         'Helvetica'
       ],
       cacheStrategy: 'aggressive',
       fallbackStrategy: 'immediate'
     });
   });
 
   // Version Modal State
   const [showVersionModal, setShowVersionModal] = useState(false);
   const [pendingVersionContent, setPendingVersionContent] = useState<string | null>(null);
 
   // Document API for checkout operations
   const documentAPI = useDocumentAPI();
   
   // Checkout status for save routing
   const { checkoutState } = useWebSocketCheckout({ documentId: documentId || '' });
   
 
   // Input management system with SimplifiedCaretSystem integration
   const [inputManager] = useState(() => new InputManager());
   const [syncManager] = useState(() => new SyncManager());
   // Create minimal renderer adapter for collaboration bridge
   const [collaborationBridge] = useState(() => {
     const minimalRenderer = {
       getCanvas: () => canvasRef.current!,
       getContext: () => canvasRef.current?.getContext('2d')!,
       requestRender: () => {
         const paperCanvasAPI = (canvasRef.current as any)?.paperCanvasAPI;
         if (paperCanvasAPI) {
           paperCanvasAPI.render();
         }
       },
       getCharacterPosition: (index: number) => {
         const paperCanvasAPI = (canvasRef.current as any)?.paperCanvasAPI;
         return paperCanvasAPI?.getCharacterPosition?.(index);
       },
       setDocumentState: (state: any) => {
         const paperCanvasAPI = (canvasRef.current as any)?.paperCanvasAPI;
         if (paperCanvasAPI) {
           paperCanvasAPI.setDocumentState(state);
         }
       },
       setBackendLayoutPlan: (plan: any) => {
         const paperCanvasAPI = (canvasRef.current as any)?.paperCanvasAPI;
         if (paperCanvasAPI) {
           paperCanvasAPI.setBackendLayoutPlan(plan);
         }
       },
       updateRemoteCursor: (userId: string, position: any) => {
         const paperCanvasAPI = (canvasRef.current as any)?.paperCanvasAPI;
         if (paperCanvasAPI) {
           paperCanvasAPI.updateRemoteCursor(userId, position);
         }
       },
       removeRemoteCursor: (userId: string) => {
         const paperCanvasAPI = (canvasRef.current as any)?.paperCanvasAPI;
         if (paperCanvasAPI) {
           paperCanvasAPI.removeRemoteCursor(userId);
         }
       }
     };
     return new CollaborationIntegrationBridge(documentEngine, minimalRenderer);
   });
   const [legalCompliance] = useState(() => new LegalComplianceEngine());
 
   // Remove direct per-keystroke layout requests; rely on SyncManager debounced events
   const [importExportManager] = useState(() => new DocumentImportExportManager());
   const [performanceOptimizer] = useState(() => new LargeDocumentOptimizer());
   
   // DMS Integration services - fix circular dependencies with useEffect
   const [dmsService, setDmsService] = useState<DMSDocumentService | null>(null);
   // const [autoSaveManager, setAutoSaveManager] = useState<AutoSaveManager | null>(null); // Commented out - using manual save only
   const [lifecycleManager, setLifecycleManager] = useState<DocumentLifecycleManager | null>(null);
   
   // Online/Offline Save Routing
   const [connectivityManager, setConnectivityManager] = useState<ConnectivityManager | null>(null);
   const [saveRoutingManager, setSaveRoutingManager] = useState<SaveRoutingManager | null>(null);
 
   // Parse document version from API response
   const parseDocumentVersion = (documentData: any): number => {
     // Based on API test results, version is in latest_version.version_number as string "v0.0"
     const versionString = documentData?.latest_version?.version_number || 
                          documentData?.version_number || 
                          documentData?.version || 
                          'v0.0';
     
     
     if (typeof versionString === 'number') {
       return versionString;
     }
     
     if (typeof versionString === 'string') {
       // Handle "v0.0", "v1.2", "1.0" formats
       const match = versionString.match(/^v?(\d+)(?:\.(\d+))?/);
       if (match) {
         const major = parseInt(match[1], 10);
         const minor = match[2] ? parseInt(match[2], 10) : 0;
         // Convert to single integer: v1.2 -> 12, v0.0 -> 0
         const numericVersion = major * 10 + minor;
         return numericVersion;
       }
     }
     
     console.warn('[ClientFirstDocumentEditor] Invalid version format, defaulting to 0:', versionString);
     return 0;
   };
 
   // Initialize services in proper order
   useEffect(() => {
     // Auto-derive production defaults if props are not provided
     let baseUrl = dmsBaseUrl;
     let token = authToken as string | undefined;
     try {
       if (!baseUrl) baseUrl = window.location.origin;
       if (!token) token = localStorage.getItem('webelieve_access_token') || localStorage.getItem('access_token') || undefined as any;
     } catch {}
 
     if (baseUrl && token) {
       const dms = new DMSDocumentService(baseUrl, token);
       setDmsService(dms);
       
       // Initialize connectivity manager for online/offline detection
       const connectivity = new ConnectivityManager(baseUrl, token);
       setConnectivityManager(connectivity);
       
       // Initialize save routing manager for online/offline save logic
       const saveRouting = new SaveRoutingManager(connectivity, dms);
       setSaveRoutingManager(saveRouting);
       
       // AutoSave removed - using manual save only
       // const autoSave = new AutoSaveManager(dms, connectivity);
       // setAutoSaveManager(autoSave);
       
       const lifecycle = new DocumentLifecycleManager(dms, null);
       setLifecycleManager(lifecycle);
     }
   }, [dmsBaseUrl, authToken]);
 
   // Phase 1: Backend Service Integration - Initialize HarfBuzz and Redis services
   useEffect(() => {
     const initializeBackendServices = async () => {
       try {
         
         // REMOVED: Frontend HarfBuzz client - Use backend-only architecture
         // const harfbuzzClient = createHarfBuzzClient({
         //   baseUrl: import.meta.env.VITE_HARFBUZZ_URL || 'http://localhost:8081',
         //   batchSize: 50,
         //   enableBatching: true,
         //   timeout: 30000
         // });
         
         // Create Redis cache service (PRODUCTION: Backend Redis enabled by default)
         const cacheService = createRedisCacheService({
           keyPrefix: 'edgelex:harfbuzz:',
           l1CacheSize: 1000,
           enableCompression: true,
           compressionThreshold: 1024,
           // Backend Redis integration (enabled by default)
           backendUrl: window.location.origin,
           enableBackendCache: true
         });
         
        // Create backend font service if it doesn't exist
        const backendFontService = new BackendFontService(
          dmsBaseUrl || 'http://localhost:3000',
          authToken || ''
        );
         
         // Initialize backend services in LocalDocumentEngine (NO frontend HarfBuzz client)
         await documentEngine.initializeBackendServices({
           // harfbuzzClient, // REMOVED: Frontend HarfBuzz client
           cacheService,
           fontManager,
           backendFontService
         });
         
         
       } catch (error) {
         console.error('[Phase1] ❌ Backend service initialization failed:', error);
         // Continue without backend services - graceful degradation
       }
     };
     
     // Only initialize if documentEngine and fontManager are available
     if (documentEngine && fontManager) {
       initializeBackendServices();
     }
   }, [documentEngine, fontManager]);
 
   // Phase 1: FIXED Optimized React Hooks for Backend Service State Management
   const [engineState, engineActions, errorState] = useDocumentEngineStateOptimized(documentEngine, {
     batchDelay: 16, // 60fps batching
     enablePerformanceMonitoring: true,
     enableErrorBoundary: true
   });
   const harfbuzzData = useHarfBuzzMetricsOptimized(documentEngine);
   const performanceData = useDocumentEnginePerformanceOptimized(documentEngine);
   const backendServiceStatus = useBackendServiceStatusOptimized(documentEngine);
   
   // Phase 2: Cache metrics integration - FIXED AND RE-ENABLED
   const cacheMetrics = useCacheMetricsOptimized(documentEngine);
   
   // Legacy hooks (for comparison during Phase 1 testing)
   // const [legacyEngineState, legacyEngineActions] = useDocumentEngineState(documentEngine);
   // const { metrics: legacyHarfbuzzMetrics } = useHarfBuzzMetrics(documentEngine);
   // const { performanceMetrics: legacyPerformanceMetrics } = useDocumentEnginePerformance(documentEngine);
   
   // DMS state
   const [dmsState, setDmsState] = useState({
     isInitialized: false,
     currentDocumentId: documentId || null,
     isDirty: false,
     lastSaveTime: null as Date | null,
     autoSaveEnabled: enableAutoSave,
     isCreatingDocument: false,
     isSaving: false,
     isCreatingVersion: false
   });
   
   // Editor state
   const [editorState, setEditorState] = useState<EditorState>({
     isInitialized: false,
     isConnected: false,
     isLoading: false, // Start with false so canvas renders immediately
     collaborators: [],
     currentOperation: null,
     lastSyncTime: null,
     performanceMetrics: null,
     legalAnalysis: null,
     fontLoadingState: {
       loadedFonts: new Set(),
       loadingFonts: new Set(),
       failedFonts: new Set(),
       harfbuzzReady: false,
       msdfAtlasesReady: false,
       systemCapabilities: {
         hasWebFonts: 'fonts' in document,
         hasVariableFonts: CSS.supports('font-variation-settings', '"wght" 400'),
         hasOpenTypeFeatures: CSS.supports('font-feature-settings', '"kern" 1'),
         hasColorFonts: CSS.supports('font-palette', 'normal'),
         maxFontSize: 1000,
         supportedFormats: ['woff2', 'woff', 'ttf', 'otf']
       }
     },
     currentFontRequirements: null
   });
 
   // Page Setup state
   const [isPageSetupOpen, setIsPageSetupOpen] = useState(false);
   
  // Text Formatting state
  const [hasTextFormatting, setHasTextFormatting] = useState(false);
  
  // Selection state for TextFormattingIntegration
  const [selectionStart, setSelectionStart] = useState<number>(-1);
  const [selectionEnd, setSelectionEnd] = useState<number>(-1);
   const [paperDimensions, setPaperDimensions] = useState({
     paperWidth: 816,
     paperHeight: 1056,
     contentX: 24,  // 0.25" at 96 DPI
     contentY: 24,  // 0.25" at 96 DPI
     contentWidth: 768, // 816 - 24 - 24
     contentHeight: 1008, // 1056 - 24 - 24
     margins: {
       top: 24,   // 0.25" at 96 DPI
       right: 24, // 0.25" at 96 DPI
       bottom: 24, // 0.25" at 96 DPI
       left: 24   // 0.25" at 96 DPI
     },
     dpi: 96,
     orientation: 'portrait' as const,
     pageSize: 'Letter'
   });
   
   // Performance Panel state - REMOVED: Moved to Analytics Dashboard
   
  // Backend layout plan state
  const [currentBackendLayoutPlan, setCurrentBackendLayoutPlan] = useState<any>(null);
  
  // Document state for PaperCanvas
  const [currentDocumentState, setCurrentDocumentState] = useState<any>(null);
   // 🎯 RACE CONDITION FIX: Track layout plan sequence to prevent out-of-order application
   const [lastAppliedSequence, setLastAppliedSequence] = useState<number>(-1);
 
   // Prevent multiple simultaneous initialization attempts
   const initializationInProgress = useRef(false);
 
   // ============================================================================
   // PERFORMANCE MONITORING
   // ============================================================================
 
   // Performance monitoring effect - updates every 5 seconds to prevent render loops
   useEffect(() => {
     // if (!canvasRenderer) return; // Commented out per user request
     
     const performanceInterval = setInterval(() => {
       try {
         // const typographyMetrics = canvasRenderer.getTypographyPerformanceMetrics(); // Commented out per user request
         // const basicMetrics = canvasRenderer.getPerformanceMetrics(); // Commented out per user request
         
         // Only update state if there are significant changes
         // const newFrameRate = basicMetrics.frameRate; // Commented out per user request
         const currentFrameRate = editorState.performanceMetrics?.frameRate || 0;
         
         // if (Math.abs(newFrameRate - currentFrameRate) > 5) { // Only update if frame rate changed by >5fps - commented out per user request
         // const enhancedMetrics: EnhancedPerformanceMetrics = {
         //   renderTime: basicMetrics.renderTime,
         //     frameRate: newFrameRate,
         //   typographyMetrics,
         //   qualityMetrics: {
         //       textRenderingQuality: typographyMetrics.isOptimal ? 'high' : 'basic',
         //       scalingArtifacts: 0,
         //       subpixelAccuracy: 0.95
         //   },
         //   systemCapabilities: {
         //       hasHarfBuzz: false,
         //       hasMSDF: false,
         //       hasWebGL: (() => {
         //         try {
         //           const c = document.createElement('canvas');
         //           const gl = c.getContext('webgl', { failIfMajorPerformanceCaveat: true } as any)
         //             || c.getContext('experimental-webgl', { failIfMajorPerformanceCaveat: true } as any);
         //           if (!gl) return false;
         //           try {
         //             const renderer = (gl as any).getParameter((gl as any).RENDERER) || '';
         //             return !/SwiftShader|Software/i.test(String(renderer));
         //           } catch { return true; }
         //         } catch { return false; }
         //       })(),
         //       supportedScripts: ['Latn']
         //   }
         // };
         // 
         // setEditorState(prev => ({
         //   ...prev,
         //   performanceMetrics: enhancedMetrics
         // }));
         // }
       } catch (error) {
         console.warn('[ClientFirstDocumentEditor] Performance monitoring error:', error);
       }
     }, 5000); // Update every 5 seconds instead of 2
 
     return () => clearInterval(performanceInterval);
   }, []); // Remove canvasRenderer dependency since it's commented out
 
   // ============================================================================
   // FONT LOADING & MANAGEMENT
   // ============================================================================
 
   // Font requirements effect - updates when jurisdiction or document type changes
   useEffect(() => {
     const determineFontRequirements = (): LegalDocumentFontRequirements => {
       const jurisdictionMap: Record<string, keyof typeof LEGAL_FONT_STANDARDS> = {
         'federal': 'FEDERAL_COURTS',
         'california': 'CALIFORNIA_COURTS',
         'new-york': 'NEW_YORK_COURTS',
         'texas': 'TEXAS_COURTS',
         'florida': 'FLORIDA_COURTS'
       };
 
       const standardKey = jurisdictionMap[jurisdiction] || 'FEDERAL_COURTS';
       const standard = LEGAL_FONT_STANDARDS[standardKey];
 
       return {
         jurisdiction: jurisdiction as any,
         documentType: documentType as any,
         requiredFonts: [standard.primaryFont],
         fontSize: {
           min: 10,
           max: 24,
           default: standard.fontSize
         },
         lineSpacing: {
           min: 1.0,
           max: 3.0,
           default: standard.lineSpacing
         },
         margins: standard.margins
       };
     };
 
     const requirements = determineFontRequirements();
     
    setEditorState(prev => ({
      ...prev,
      requiredFonts: requirements.requiredFonts,
      fontSize: requirements.fontSize.default
    }));
   }, [jurisdiction, documentType]);
   // Font loading effect - loads required fonts based on current requirements
   useEffect(() => {
     if (!editorState.currentFontRequirements) return;
 
     const loadRequiredFonts = async () => {
       const { requiredFonts } = editorState.currentFontRequirements!;
       
       setEditorState(prev => ({
         ...prev,
         fontLoadingState: {
           ...prev.fontLoadingState,
           loadingFonts: new Set([...prev.fontLoadingState.loadingFonts, ...requiredFonts])
         }
       }));
 
 
       // Initialize backend font service if DMS is available
       if (dmsBaseUrl && authToken) {
         try {
           await fontManager.initializeBackendFonts(dmsBaseUrl, authToken);
         } catch (error) {
           console.warn('[ClientFirstDocumentEditor] Backend font service initialization failed:', error);
         }
       }
 
       for (const fontFamily of requiredFonts) {
         try {
           const fontSpec = {
             family: fontFamily,
             weight: 'normal' as const,
             style: 'normal' as const,
             size: editorState.currentFontRequirements!.fontSize.default
           };
 
           const loadedFont = await fontManager.loadFont(fontSpec);
           
           setEditorState(prev => ({
             ...prev,
             fontLoadingState: {
               ...prev.fontLoadingState,
               loadedFonts: new Set([...prev.fontLoadingState.loadedFonts, loadedFont.family]),
               loadingFonts: new Set([...prev.fontLoadingState.loadingFonts].filter(f => f !== fontFamily))
             }
           }));
 
         } catch (error) {
           
           setEditorState(prev => ({
             ...prev,
             fontLoadingState: {
               ...prev.fontLoadingState,
               failedFonts: new Set([...prev.fontLoadingState.failedFonts, fontFamily]),
               loadingFonts: new Set([...prev.fontLoadingState.loadingFonts].filter(f => f !== fontFamily))
             }
           }));
         }
       }
 
       // Check HarfBuzz readiness after loading fonts
       const loadingMetrics = fontManager.getLoadingMetrics();
       const harfbuzzReady = loadingMetrics.loadedFontsCount > 0 && loadingMetrics.successRate > 0.8;
       
      setEditorState(prev => ({
        ...prev,
        fontLoadingState: {
          ...prev.fontLoadingState,
          loadedCount: loadingMetrics.loadedFontsCount,
          successRate: loadingMetrics.successRate,
          harfbuzzReady
        }
      }));
     };
 
     loadRequiredFonts();
   }, [editorState.currentFontRequirements, fontManager]);
 
   // ============================================================================
   // INITIALIZATION
   // ============================================================================
 
   // Initialize after component mounts and canvas is rendered
   useEffect(() => {
     
     // Use setTimeout to wait for React to render the canvas element
     const timeoutId = setTimeout(() => {
       
       if (canvasRef.current && !editorState.isInitialized) {
         // Don't set isLoading=true here as it would hide the canvas and break initialization
         initializeEditor();
       } else {
         console.error('[ClientFirstDocumentEditor] Canvas still not available after render. Canvas exists:', !!canvasRef.current);
       }
     }, 100); // Single 100ms delay to allow React to render
     
     return () => {
       clearTimeout(timeoutId);
       destroyEditor();
     };
   }, []); // Empty dependency array - run once on mount
 
   // DMS Integration initialization
   useEffect(() => {
     const debugEnabled = false;
     if (debugEnabled) {
       console.log('🚨 [DMS_USEEFFECT] DMS useEffect triggered');
       console.log('🚨 [DMS_USEEFFECT] dmsBaseUrl:', dmsBaseUrl);
       console.log('🚨 [DMS_USEEFFECT] authToken exists:', !!authToken);
       console.log('🚨 [DMS_USEEFFECT] documentEngine exists:', !!documentEngine);
       console.log('🚨 [DMS_USEEFFECT] lifecycleManager exists:', !!lifecycleManager);
       console.log('🚨 [DMS_USEEFFECT] syncManager exists:', !!syncManager);
       // console.log('🚨 [DMS_USEEFFECT] canvasRenderer exists:', !!canvasRenderer); // Commented out per user request
     }
     
 
     const initializeDMS = async () => {
       // Resolve baseUrl/token using auto-derive fallback
       let baseUrl = dmsBaseUrl as string | undefined;
       let token = authToken as string | undefined;
       try {
         if (!baseUrl) baseUrl = window.location.origin;
         if (!token) token = localStorage.getItem('webelieve_access_token') || localStorage.getItem('access_token') || undefined as any;
       } catch {}
 
       if (!baseUrl || !token || !documentEngine || !lifecycleManager) {
         console.log('🚨 [DMS_USEEFFECT] Missing requirements:', {
           dmsBaseUrl: !!baseUrl,
           authToken: !!token,
           documentEngine: !!documentEngine,
           lifecycleManager: !!lifecycleManager
         });
         return;
       }
 
       try {
         
         // Set document ID in engine for interface compliance
         console.log('📋 [DOC_ID_TRACE] Setting document ID in engine:', documentId);
         if (documentId) {
           documentEngine.setCurrentDocumentId(documentId);
           console.log('📋 [DOC_ID_TRACE] ✅ Document ID set in engine');
         } else {
           console.warn('📋 [DOC_ID_TRACE] ⚠️ No document ID provided!');
         }
         
         // Initialize enhanced backend in sync manager
         if (syncManager) {
           syncManager.initializeEnhancedBackend(baseUrl, token);
         }
 
         // 🎯 DIAGNOSTIC: Check collaboration state before backend layout
         console.log('🤝 [COLLAB_STATE] Checking collaboration system state...');
         console.log('🤝 [COLLAB_STATE] SyncManager exists:', !!syncManager);
         // Enhanced PaperCanvas handles rendering internally
         // console.log('🤝 [COLLAB_STATE] CollaborationBridge exists:', !!collaborationBridge); // Commented out per user request
         console.log('📋 [DOC_ID_TRACE] Current document ID in props:', documentId);
         console.log('📋 [DOC_ID_TRACE] Document ID in engine:', documentEngine?.getCurrentDocumentId?.());
         
         // 🎯 BACKEND LAYOUT SETUP: Enable server-side Times New Roman typesetting
         if (syncManager) { // Removed canvasRenderer check per user request
           console.log('🚀 [BACKEND_LAYOUT] Enabling server-side Times New Roman typesetting...');
           console.log('🚀 [BACKEND_LAYOUT] DMS Base URL:', baseUrl);
           console.log('🚀 [BACKEND_LAYOUT] Auth Token Available:', !!token);
           
           // Enable backend layout in canvas renderer
           // Enhanced PaperCanvas uses backend layout by default when available
           
           // 🎯 DECOUPLED: Direct layout plan requests instead of corrupted event system
           console.log('🚀 [DECOUPLED] Using direct layout plan requests (bypassing old sync events)');
           
           // Request initial layout plan for current document content
           console.log('🚀 [BACKEND_LAYOUT] Requesting initial layout plan from server...');
           try {
             const initialLayoutPlan = await syncManager.requestLayoutPlan(true);
             if (initialLayoutPlan && initialLayoutPlan.pages) {
               console.log('🎉 [BACKEND_LAYOUT] ✅ SERVER LAYOUT PLAN LOADED SUCCESSFULLY!');
               console.log('🎉 [BACKEND_LAYOUT] Using server-side Times New Roman typography');
               
               // Add visual font confirmation
               console.log('🔤 [FONT_VERIFICATION] Backend layout plan font details:');
               console.log('🔤 [FONT_VERIFICATION] Page setup font hints:', initialLayoutPlan.pageSetup?.fontUsageHints);
               console.log('🔤 [FONT_VERIFICATION] Layout font hints:', initialLayoutPlan.fontUsageHints);
               
               // Enhanced PaperCanvas handles layout plans via props
         const paperCanvasAPI = (canvasRef.current as any)?.paperCanvasAPI;
         if (paperCanvasAPI) {
           paperCanvasAPI.setBackendLayoutPlan(initialLayoutPlan);
         }
         // Update state for prop passing
         setCurrentBackendLayoutPlan(initialLayoutPlan);
               console.log('✅ [BACKEND_LAYOUT] Backend layout system initialized with inline renderer');
             } else {
               console.error('❌ [BACKEND_LAYOUT] Failed to get layout plan from server - falling back to client-side');
               console.error('❌ [BACKEND_LAYOUT] Layout plan validation failed:', { 
                 hasLayoutPlan: !!initialLayoutPlan, 
                 hasPages: !!(initialLayoutPlan && initialLayoutPlan.pages) 
               });
               // Keep backend layout enabled but without initial plan
             }
           } catch (error) {
             console.error('❌ [BACKEND_LAYOUT] Error requesting initial layout plan:', error);
             // Keep backend layout enabled but log the error
           }
         } else {
           console.error('❌ [BACKEND_LAYOUT] Cannot enable - missing syncManager');
         }
         
         // 🚨 CRITICAL FIX: Initialize collaboration BEFORE loading document to prevent TS_DELTA_NO_TARGET
         console.log('🔄 [COLLABORATION_FIRST] Initializing collaboration BEFORE document loading...');
         await initializeCollaborationFirst(documentId, baseUrl, token);
         
         // Load existing document or prepare for new document creation
         if (documentId) {
           // NEW ARCHITECTURE: Only load working copy if offline or if there are pending changes
           const shouldLoadWorkingCopy = connectivityManager?.isOffline() || 
                                        (saveRoutingManager?.getPendingWorkingCopiesCount() || 0) > 0;
           
           const result = await lifecycleManager.loadExistingDocument(documentId, documentEngine, {
             loadWorkingCopy: shouldLoadWorkingCopy
           });
           
           if (result.success && result.data) {
             const { document, textContent, pageSetupMetadata } = result.data;
             
             // 🚨 CRITICAL DEBUG: Log pageSetupMetadata extraction
             
             // CRITICAL FIX: Document engine is already initialized by lifecycleManager.loadExistingDocument()
             // No need to initialize again - just update canvas renderer with loaded content
             console.log('🎯 [DMS_LOAD_FIX] Document loaded by lifecycle manager');
             console.log('🎯 [DMS_LOAD_FIX] Text length:', textContent.length);
             
             // 💾 RESTORE DPI: Apply saved page setup metadata from database
             if (pageSetupMetadata) {
               console.log('💾 [DPI_RESTORE] Found saved page setup metadata:', pageSetupMetadata);
               console.log('🔧 [FONT_RESTORE] Font settings in metadata:', {
                 fontFamily: pageSetupMetadata.fontFamily,
                 fontSize: pageSetupMetadata.fontSize,
                 fontColor: pageSetupMetadata.fontColor
               });
               
               if (pageSetupMetadata.dpi) {
                 console.log(`💾 [DPI_RESTORE] Restoring DPI from database: ${pageSetupMetadata.dpi}`);
                 
                 // Update paperDimensions with saved DPI
                 const restoredDimensions = calculatePaperDimensionsFromPageSetup({
                   ...pageSetupMetadata,
                   pageSize: pageSetupMetadata.pageSize || 'Letter'
                 });
                 
                 setPaperDimensions(restoredDimensions);
                 console.log('✅ [DPI_RESTORE] Paper dimensions updated with saved DPI:', restoredDimensions.dpi);
                 
                 // Update SyncManager with saved page setup
                 if (syncManager) {
                   await syncManager.updatePageSetup(pageSetupMetadata);
                   console.log('✅ [DPI_RESTORE] SyncManager updated with saved page setup');
                 }
                 
                 // Restore character formatting if available
                 if (pageSetupMetadata.characterFormatting && (canvasRef.current as any)?.formattingAPI) {
                   console.log('🎨 [CHARACTER_FORMATTING] Restoring formatting data:', pageSetupMetadata.characterFormatting);
                   (canvasRef.current as any).formattingAPI.loadFormattingData(pageSetupMetadata.characterFormatting);
                   console.log('✅ [CHARACTER_FORMATTING] Character formatting restored');
                 }
               }
             } else {
               console.log('📋 [DPI_RESTORE] No page setup metadata found, using defaults');
             }
             
             // Enhanced PaperCanvas handles rendering internally
             console.log('🎯 [DMS_LOAD_FIX] Editor initialized:', editorState.isInitialized);
             
             if (editorState.isInitialized) {
               // Enhanced PaperCanvas receives document state via props
         const paperCanvasAPI = (canvasRef.current as any)?.paperCanvasAPI;
         if (paperCanvasAPI) {
           paperCanvasAPI.setDocumentState({
             text: documentEngine.getText(),
             caretPosition: 0,
             selection: null,
             backendLayoutPlan: null
           });
         }
               console.log('🎯 [DMS_LOAD_FIX] ✅ Inline renderer updated with loaded document content');
               
               // 🚀 CRITICAL FIX: Request initial layout after document loads
               if (syncManager) {
                 console.log('🚀 [INITIAL_LAYOUT] Requesting initial layout for loaded document');
                 // Force layout request with current document content
                 setTimeout(async () => {
                   try {
                     await (syncManager as any).requestLayoutPlanCoordinated(true);
                     console.log('✅ [INITIAL_LAYOUT] Initial layout request completed');
                   } catch (error) {
                     console.error('❌ [INITIAL_LAYOUT] Initial layout request failed:', error);
                   }
                 }, 50); // Small delay to ensure all initialization is complete
               }
             } else {
               console.warn('🎯 [DMS_LOAD_FIX] ⚠️ Editor not ready:', {
                 // Enhanced PaperCanvas handles rendering internally
                 editorInitialized: editorState.isInitialized
               });
               
               // Retry after a short delay if editor isn't ready
               setTimeout(() => {
                 if (editorState.isInitialized) {
                   console.log('🎯 [DMS_LOAD_FIX] 🔄 Retrying inline renderer update...');
                   // Enhanced PaperCanvas receives document state via props
         const paperCanvasAPI = (canvasRef.current as any)?.paperCanvasAPI;
         if (paperCanvasAPI) {
           paperCanvasAPI.setDocumentState({
             text: documentEngine.getText(),
             caretPosition: 0,
             selection: null,
             backendLayoutPlan: null
           });
         }
                   console.log('🎯 [DMS_LOAD_FIX] ✅ Delayed inline renderer update completed');
                   
                   // 🚀 CRITICAL FIX: Request initial layout after delayed document load
                   if (syncManager) {
                     console.log('🚀 [INITIAL_LAYOUT] Requesting initial layout for delayed loaded document');
                     setTimeout(async () => {
                       try {
                         await (syncManager as any).requestLayoutPlanCoordinated(true);
                         console.log('✅ [INITIAL_LAYOUT] Delayed initial layout request completed');
                       } catch (error) {
                         console.error('❌ [INITIAL_LAYOUT] Delayed initial layout request failed:', error);
                       }
                     }, 50);
                   }
                 }
               }, 100);
             }
           }
           
           // Enable auto-save for existing document
           // if (enableAutoSave && autoSaveManager) {
           //   autoSaveManager.startAutoSave(documentId, documentEngine);
           // } // Commented out - using manual save only
         } else if (clientId && matterId && documentTitle) {
           setDmsState(prev => ({ ...prev, isCreatingDocument: true }));
         }
         
         // Set up document change listeners
         documentEngine.on('document-saved', (data: any) => {
           setDmsState(prev => ({ 
             ...prev, 
             lastSaveTime: new Date(),
             isDirty: false,
             isSaving: false 
           }));
           onDocumentSaved?.(data.documentId);
         });
         
         documentEngine.on('version-created', (data: any) => {
           setDmsState(prev => ({ 
             ...prev, 
             lastSaveTime: new Date(),
             isDirty: false,
             isSaving: false 
           }));
           onVersionCreated?.(data.versionId);
         });
         
         documentEngine.on('document-created', (data: any) => {
           setDmsState(prev => ({ 
             ...prev, 
             currentDocumentId: data.documentId,
             isCreatingDocument: false,
             lastSaveTime: new Date(),
             isDirty: false 
           }));
           onDocumentSaved?.(data.documentId);
         });
         
         // Layout plan listener will be set up in backend layout initialization section
         
         setDmsState(prev => ({ ...prev, isInitialized: true }));
         
       } catch (error) {
         console.error('[ClientFirstDocumentEditor] Failed to initialize DMS:', error);
         onError?.(error as Error);
       }
     };
 
     // Only initialize DMS after the main editor is initialized
     if (editorState.isInitialized && !dmsState.isInitialized) {
       initializeDMS();
     }
     
     if (debugEnabled)   console.log('🚨 [DMS_USEEFFECT] DMS useEffect completed');
   }, [editorState.isInitialized, dmsState.isInitialized, dmsBaseUrl, authToken, documentId, clientId, matterId, documentTitle]);
 
   // Save Document Handler
   const handleSave = useCallback(async () => {
     if (!lifecycleManager || !documentId || dmsState.isSaving) {
       return;
     }
 
     setDmsState(prev => ({ ...prev, isSaving: true }));
 
     try {
       
       // Get current document content
       const currentContent = documentEngine.getText();
       
       // Save using lifecycle manager (will trigger content-aware activity tracking)
       // NOTE: Saving does NOT require checkout - checkout is only for exclusive editing locks
       
       // 💾 INCLUDE PAGE SETUP: Save current page setup metadata with content
       const currentPageSetup = syncManager?.getBackendPageSetup?.() || {
         dpi: paperDimensions.dpi,
         pageSize: paperDimensions.pageSize,
         margins: paperDimensions.margins,
         orientation: paperDimensions.orientation
       };
       
       // 🔧 CRITICAL FIX: Get current font settings from SyncManager
       const currentFontSettings = (syncManager as any)?.currentFontSettings || {};
       
       const result = await lifecycleManager.saveDocument(documentId, {
         forceImmediate: true,
         changeSummary: 'Manual save',
         metadata: {
           pageSetup: currentPageSetup,
           lastModified: new Date().toISOString(),
           saveType: 'manual'
         }
       });
 
       // 💾 CRITICAL FIX: Also save page setup metadata to AST API (same as APPLY button)
       if (result.success && documentId && versionId && currentPageSetup.dpi) {
         console.log('💾 [SAVE_DPI_PERSISTENCE] Saving page setup metadata via AST API');
         try {
           const token = localStorage.getItem('webelieve_access_token') || localStorage.getItem('access_token') || auth.token;
           
           // Get current AST
           const astResponse = await fetch(`/api/v1/dms/documents/${documentId}/versions/${versionId}/ast`, {
             headers: { 'Authorization': `Bearer ${token}` }
           });
           
           let currentAST = {};
           if (astResponse.ok) {
             const astData = await astResponse.json();
             currentAST = astData.ast || {};
           }
           
           // Get character formatting data
           const formattingData = (canvasRef.current as any)?.formattingAPI?.getFormattingData();
           console.log('💾 [CHARACTER_FORMATTING] Saving formatting data:', formattingData);
           
           // Add page setup metadata and character formatting to AST
           const updatedAST = {
             ...currentAST,
             metadata: {
               ...(currentAST as any).metadata,
               pageSetup: {
                 dpi: currentPageSetup.dpi,
                 pageSize: currentPageSetup.pageSize,
                 margins: currentPageSetup.margins,
                 orientation: (currentPageSetup as any).orientation || 'portrait',
                 fontFamily: currentFontSettings.fontFamily || (currentPageSetup as any).fontFamily || 'Times New Roman',
                 fontSize: currentFontSettings.fontSize || (currentPageSetup as any).fontSize || 12,
                 fontColor: currentFontSettings.fontColor || '#000000',
                 lastModified: new Date().toISOString()
               },
               characterFormatting: formattingData
             }
           };
           
           // Save updated AST with metadata
           const saveResponse = await fetch(`/api/v1/dms/documents/${documentId}/versions/${versionId}/ast`, {
             method: 'POST',
             headers: {
               'Content-Type': 'application/json',
               'Authorization': `Bearer ${token}`
             },
             body: JSON.stringify({
               ast: updatedAST,
               ast_version: 'v1'
             })
           });
           
           if (saveResponse.ok) {
             console.log('✅ [SAVE_DPI_PERSISTENCE] Page setup metadata saved to AST successfully');
           } else {
             console.error('❌ [SAVE_DPI_PERSISTENCE] Failed to save AST metadata:', saveResponse.statusText);
           }
         } catch (error) {
           console.error('❌ [SAVE_DPI_PERSISTENCE] Error saving page setup metadata:', error);
         }
       }
 
       if (result.success) {
         setDmsState(prev => ({ 
           ...prev, 
           isDirty: false, 
           lastSaveTime: new Date(),
           isSaving: false 
         }));
         
         // Show success toast
         const event = new CustomEvent('show-toast', {
           detail: {
             message: 'Document saved successfully!',
             type: 'success'
           }
         });
         window.dispatchEvent(event);
 
         // Notify parent component
         if (onDocumentSaved) {
           onDocumentSaved(documentId);
         }
       } else {
         throw new Error(result.message || 'Save failed');
       }
     } catch (error) {
       console.error('❌ [ClientFirstDocumentEditor] Save failed:', error);
       
       setDmsState(prev => ({ ...prev, isSaving: false }));
       
       // Show error toast
       const event = new CustomEvent('show-toast', {
         detail: {
           message: `Save failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
           type: 'error'
         }
       });
       window.dispatchEvent(event);
     }
   }, [lifecycleManager, documentId, dmsState.isSaving, documentEngine, onDocumentSaved, documentAPI]);
 
   // New Version Handler
   const handleNewVersion = useCallback(async () => {
     if (!documentId || dmsState.isCreatingVersion) {
       return;
     }
 
     try {
       
       // Get current document content for the modal
       const currentContent = documentEngine.getText();
       setPendingVersionContent(currentContent);
       setShowVersionModal(true);
       
     } catch (error) {
       console.error('❌ [ClientFirstDocumentEditor] Failed to prepare version creation:', error);
       
       // Show error toast
       const event = new CustomEvent('show-toast', {
         detail: {
           message: `Failed to prepare version creation: ${error instanceof Error ? error.message : 'Unknown error'}`,
           type: 'error'
         }
       });
       window.dispatchEvent(event);
     }
   }, [documentId, dmsState.isCreatingVersion, documentEngine]);
 
   // Version Decision Handler
   const handleVersionDecision = useCallback(async (decision: any) => {
     if (decision.action === 'cancel') {
       setShowVersionModal(false);
       setPendingVersionContent(null);
       return;
     }
 
     if (!lifecycleManager || !documentId || !pendingVersionContent) {
       return;
     }
 
     setDmsState(prev => ({ ...prev, isCreatingVersion: true }));
 
     try {
       
       // Create new version using lifecycle manager (will trigger content-aware activity tracking)
       const result = await lifecycleManager.createNewVersion(
         decision.versionDescription || 'New version'
       );
 
       if (result.success) {
         setShowVersionModal(false);
         setPendingVersionContent(null);
         setDmsState(prev => ({ 
           ...prev, 
           isCreatingVersion: false,
           isDirty: false,
           lastSaveTime: new Date()
         }));
         
         // Show success toast
         const event = new CustomEvent('show-toast', {
           detail: {
             message: 'New version created successfully!',
             type: 'success'
           }
         });
         window.dispatchEvent(event);
 
         // Notify parent component
         if (onVersionCreated) {
           onVersionCreated(result.versionId || '');
         }
       } else {
         throw new Error(result.message || 'Version creation failed');
       }
     } catch (error) {
       console.error('❌ [ClientFirstDocumentEditor] Version creation failed:', error);
       
       setDmsState(prev => ({ ...prev, isCreatingVersion: false }));
       
       // Show error toast
       const event = new CustomEvent('show-toast', {
         detail: {
           message: `Version creation failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
           type: 'error'
         }
       });
       window.dispatchEvent(event);
     }
   }, [lifecycleManager, documentId, pendingVersionContent, onVersionCreated]);
 
   const initializeEditor = async (retryCount: number = 0) => {
     // Prevent multiple simultaneous initialization attempts
     if (initializationInProgress.current) {
       return;
     }
 
     try {
       initializationInProgress.current = true;
       
       // Wait for canvas to be ready with retry limit
       if (!canvasRef.current) {
         if (retryCount < 10) { // Maximum 10 retries (1 second total)
           console.warn(`[ClientFirstDocumentEditor] Canvas not ready, retrying... (${retryCount + 1}/10)`);
           initializationInProgress.current = false; // Reset flag before retry
           setTimeout(() => initializeEditor(retryCount + 1), 100);
         return;
         } else {
           throw new Error('Canvas element not available after 10 retry attempts');
         }
       }
       
       // 1. Initialize canvas rendering with inline renderer (CanvasRenderer commented out per user request)
       const canvas = canvasRef.current;
       const ctx = canvas.getContext('2d');
       if (ctx) {
         // Set up high DPI canvas
         const dpr = window.devicePixelRatio || 1;
         const rect = canvas.getBoundingClientRect();
         canvas.width = rect.width * dpr;
         canvas.height = rect.height * dpr;
         canvas.style.width = rect.width + 'px';
         canvas.style.height = rect.height + 'px';
         ctx.scale(dpr, dpr);
         console.log('✅ Inline canvas renderer initialized');
       }
       
       // CRITICAL FIX: Start render loop immediately after canvas initialization
       console.log('🎯 [RENDER_LOOP_FIX] Starting inline rendering for document loading');
       
       // Enforce declared document font (default Times New Roman) for client path
       try {
         const declaredFont = editorState.currentFontRequirements?.requiredFonts?.[0] || 'Times New Roman';
         console.log('🔤 [INLINE_RENDERER] Using font:', declaredFont);
       } catch {}
       
       // 2. Load document with automatic collaboration setup
       // Use the same token retrieval method as working useDocumentView
       const workingToken = typeof window !== 'undefined' ? (localStorage.getItem('webelieve_access_token') || localStorage.getItem('access_token') || auth.token) : auth.token;
       const documentLoadingService = new DocumentLoadingService(
         window.location.origin,
         workingToken
       );
       
       let documentData;
       try {
         documentData = await documentLoadingService.loadDocument(documentId!, attorney);
         console.log('✅ Document loaded successfully:', documentData.title);
       } catch (error) {
         console.warn('⚠️ Failed to load document from DMS, using fallback:', error);
         // Fallback to default content if document loading fails
         documentData = {
           documentId,
           title: 'New Document',
           content: initialContent ?? '',
           attorney,
           collaborationInitialized: false,
           ownerHasAccess: true,
           version: 1,
           lastModified: new Date().toISOString(),
           documentType: documentType || 'contract',
           jurisdiction: jurisdiction || 'federal'
         };
       }
 
       // 3. Initialize document engine with loaded content
       
       // Preserve empty content for new documents; do not inject demo text
       const testContent = documentData.content ?? '';
       
       const initialState: LocalDocumentState = {
         documentId: documentData.documentId,
         text: testContent,
         caretPosition: 0,
         selection: null,
         version: documentData.version,
         isDirty: false,
         created: new Date(),
         modified: new Date(),
         attorney: documentData.attorney,
         documentType: documentData.documentType as LegalDocumentType,
         jurisdiction: documentData.jurisdiction as Jurisdiction,
         lastServerSync: Date.now(),
         serverVersion: parseDocumentVersion(documentData),
         dirtyRegions: new Set<TextRange>()
       };
       
       await documentEngine.initialize(initialState);
       console.log('✅ Document engine initialized');
       
       // Connect document engine to canvas renderer (basic state sync only)
       // Enhanced PaperCanvas receives document state via props
         const paperCanvasAPI = (canvasRef.current as any)?.paperCanvasAPI;
         if (paperCanvasAPI) {
           paperCanvasAPI.setDocumentState({
             text: documentEngine.getText(),
             caretPosition: 0,
             selection: null,
             backendLayoutPlan: null
           });
         }
       
       // CRITICAL FIX: Trigger immediate render after loading document content
       console.log('🎯 [DOCUMENT_LOAD_FIX] Triggering immediate render for loaded content');
       
       // Professional layout listener removed - backend layout only
 
       // 🎯 HYBRID FIX: Listen for actual operation completion (this is where text changes happen!)
       documentEngine.on('operationCompleted', (data: { operation: any; dirtyRegion: any }) => {
         console.log('🎯 [HYBRID_FIX] operationCompleted event fired, operation type:', data.operation.type);
         
         // Only process text-changing operations
         if (data.operation.type === 'insert' || data.operation.type === 'delete') {
           const currentText = documentEngine.getState().text;
           console.log('🎯 [HYBRID_FIX] Text operation detected, current length:', currentText.length);
           
           // Update editor state
           setEditorState(prev => ({
             ...prev,
             currentOperation: data.operation
           }));
           
           // Notify parent component
           onDocumentChange?.(documentEngine.getState());
           
           // Trigger immediate client-side render
           console.log('🎯 [HYBRID_FIX] Triggering immediate client-side render');
           // Enhanced PaperCanvas handles rendering internally
         const paperCanvasAPI = (canvasRef.current as any)?.paperCanvasAPI;
         if (paperCanvasAPI) {
           paperCanvasAPI.render();
         } // Use inline renderer instead
           
           // Layout requests are now triggered centrally by SyncManager with debounce
         }
       });
       
       // 4. Initialize input handling ONLY after canvas is ready
       
       if (!canvasRef.current) {
         throw new Error('[ClientFirstDocumentEditor] Canvas ref is null during InputManager initialization');
       }
       
       // Initialize InputManager with PaperCanvas API adapter
       const inputPaperCanvasAPI = (canvasRef.current as any)?.paperCanvasAPI;
       if (inputPaperCanvasAPI) {
         // Create minimal renderer adapter for InputManager
         const inputRenderer = {
           getCanvas: () => canvasRef.current!,
           getContext: () => canvasRef.current?.getContext('2d')!,
           requestRender: () => inputPaperCanvasAPI.render(),
           getCharacterPosition: (index: number) => inputPaperCanvasAPI.getCharacterPosition?.(index),
           setDocumentState: (state: any) => inputPaperCanvasAPI.setDocumentState(state),
           setBackendLayoutPlan: (plan: any) => inputPaperCanvasAPI.setBackendLayoutPlan(plan),
           updateRemoteCursor: (userId: string, position: any) => inputPaperCanvasAPI.updateRemoteCursor(userId, position),
           removeRemoteCursor: (userId: string) => inputPaperCanvasAPI.removeRemoteCursor(userId),
           mapPointToTextIndex: (x: number, y: number) => {
             // 🎯 CRITICAL FIX: Use real HarfBuzz hit testing instead of hardcoded approximations
             if (currentBackendLayoutPlan) {
               try {
                 const contentRect = {
                   x: paperDimensions.contentX || paperDimensions.margins.left,
                   y: paperDimensions.contentY || paperDimensions.margins.top,
                   width: paperDimensions.contentWidth,
                   height: paperDimensions.contentHeight
                 };
                 
                 
                 const hitResult = HitTestingEngine.hitTest(x, y, currentBackendLayoutPlan, contentRect);
                 
                 if (hitResult) {
                   return hitResult.characterIndex;
                 } else {
                   console.warn('⚠️ [HIT_TEST_DEBUG] HitTestingEngine returned null result');
                 }
               } catch (error) {
                 console.warn('⚠️ [HIT_TEST_FIX] HitTestingEngine failed, using fallback:', error);
               }
             } else {
               console.warn('⚠️ [HIT_TEST_DEBUG] No currentBackendLayoutPlan available');
             }
             
             // Fallback: Use better approximations based on real document text
             const leftMargin = paperDimensions.margins.left;
             const topMargin = paperDimensions.margins.top;
             
             if (x < leftMargin || y < topMargin) return 0;
             
             // 🎯 CRITICAL FIX: Use document text length to constrain calculations
             const currentText = documentEngine.getText();
             const textLength = currentText.length;
             
             // Better approximations based on typical Times New Roman 12pt
             const avgCharWidth = 6; // More accurate for Times New Roman
             const lineHeight = 18; // More accurate line height
             
             const line = Math.floor((y - topMargin) / lineHeight);
             const col = Math.floor((x - leftMargin) / avgCharWidth);
             
             // Calculate approximate position but constrain to document length
             const charsPerLine = Math.floor(paperDimensions.contentWidth / avgCharWidth);
             let approximateIndex = Math.max(0, line * charsPerLine + col);
             
             // 🎯 CRITICAL: Constrain to actual document length
             approximateIndex = Math.min(approximateIndex, textLength);
             
             
             return approximateIndex;
           },
           setCaretVisible: (visible: boolean) => inputPaperCanvasAPI.setCaretVisibility?.(visible),
           // CRITICAL: Provide layout plan with correct extraction path for ProductionCaretSystem
           getLayoutPlan: () => {
             if (currentBackendLayoutPlan?.data?.layoutPlan) {
               console.log('[ProductionCaretSystem] Providing layout plan via data.layoutPlan path');
               return currentBackendLayoutPlan.data.layoutPlan;
             } else if (currentBackendLayoutPlan?.layoutPlan) {
               console.log('[ProductionCaretSystem] Providing layout plan via layoutPlan path');
               return currentBackendLayoutPlan.layoutPlan;
             } else {
               console.log('[ProductionCaretSystem] Providing entire currentBackendLayoutPlan as fallback');
               return currentBackendLayoutPlan;
             }
           }
         };
         
         // Get the caret system from PaperCanvas (it will be created there)
        const paperCanvasAPI = (canvasRef.current as any)?.paperCanvasAPI;
        const caretSystem = paperCanvasAPI?.getCaretSystem?.();
        
        await inputManager.initialize(canvasRef.current, documentEngine, inputRenderer, collaborationBridge, caretSystem);
         console.log('✅ Input manager initialized with PaperCanvas integration');
         
         // 🎯 CRITICAL: Focus the canvas to receive keyboard events
         canvasRef.current.focus();
         console.log('🎯 [INPUT_FIX] Canvas focused for keyboard input');
       } else {
         console.error('❌ PaperCanvas API not available for InputManager initialization');
       }
       
       // Set up document change tracking for dirty state
       documentEngine.on('textChanged', () => {
         setDmsState(prev => ({ ...prev, isDirty: true }));
       });
       
       // Ensure caret blinking and input focus
       try {
         canvasRef.current?.focus();
         const paperCanvasAPI = (canvasRef.current as any)?.paperCanvasAPI;
         if (paperCanvasAPI) {
           paperCanvasAPI.setFocus(true);
         }
       } catch {}
       
       // 5. Initialize synchronization
       await syncManager.initialize(documentEngine, websocketUrl, auth);
       console.log('✅ Sync manager initialized');
       
       // 5.1. Initialize enhanced backend if DMS is configured
       if (dmsBaseUrl && authToken) {
         syncManager.initializeEnhancedBackend(dmsBaseUrl, authToken);
       }
       
       // 5.1. Backend layout setup now handled in DMS useEffect for proper initialization order
       
       // 6. Initialize collaboration integration with existing system
       
       // Use the same token retrieval method for consistency
       const collaborationToken = workingToken;
       
       if (!collaborationToken) {
         console.warn('[ClientFirstDocumentEditor] No authentication token available, skipping collaboration initialization');
       } else {
         try {
           // 6.1. Auto-enable collaboration for EdgeDocs typography if not already enabled
           console.log('🔄 [EDGEDOCS_AUTO_COLLAB] Checking if collaboration needs to be enabled for typography...');
           
           try {
             const checkResponse = await fetch(`${dmsBaseUrl}/api/v1/documents/${documentId}/collaboration`, {
               headers: {
                 'Authorization': `Bearer ${collaborationToken}`,
                 'Content-Type': 'application/json',
               },
             });
             
             if (!checkResponse.ok && (checkResponse.status === 404 || checkResponse.status === 500)) {
               console.log('🚀 [EDGEDOCS_AUTO_COLLAB] Collaboration not enabled, auto-initializing for typography...');
               
               const initResponse = await fetch(`${dmsBaseUrl}/api/v1/documents/${documentId}/collaboration`, {
                 method: 'POST',
                 headers: {
                   'Authorization': `Bearer ${collaborationToken}`,
                   'Content-Type': 'application/json',
                 },
                 body: JSON.stringify({
                   collaboration_mode: 'view_only',
                   default_permissions: {
                     canEdit: false,
                     canComment: false,
                     canTrackChanges: false,
                     canAcceptReject: false,
                     canShare: false,
                     canManageUsers: false
                   },
                   allow_anonymous_comments: false,
                   require_approval_for_changes: false,
                   track_changes_enabled: false,
                   comments_enabled: false,
                   auto_enabled_for_typography: true
                 })
               });
               
               if (initResponse.ok) {
                 console.log('✅ [EDGEDOCS_AUTO_COLLAB] Collaboration auto-enabled for typography');
               } else {
                 console.warn('⚠️ [EDGEDOCS_AUTO_COLLAB] Failed to auto-enable collaboration:', initResponse.status);
               }
             } else {
               console.log('✅ [EDGEDOCS_AUTO_COLLAB] Collaboration already enabled');
             }
           } catch (autoEnableError) {
             console.warn('⚠️ [EDGEDOCS_AUTO_COLLAB] Auto-enable failed, continuing anyway:', autoEnableError);
           }
           
           // 6.2. Collaboration bridge initialization moved to initializeCollaborationFirst() to prevent duplicates
         } catch (error) {
           console.warn('[ClientFirstDocumentEditor] Collaboration initialization failed, continuing without collaboration:', error);
         }
       }
       console.log('✅ Collaboration integration initialized');
       
       // 7. Initialize legal features
       await legalCompliance.startRealTimeMonitoring(documentEngine);
       console.log('✅ Legal compliance initialized');
       
       // 8. Initialize performance optimization
       const documentState = documentEngine.getState();
       
       // Start performance monitoring
       performanceOptimizer.startPerformanceMonitoring();
       
       // Apply document optimization if needed
       if (performanceOptimizer.shouldOptimizeDocument(documentState)) {
         await performanceOptimizer.optimizeDocument(documentState);
         console.log('✅ Performance optimization applied');
       }
       
       // Enable viewport-based rendering for large documents
       if (documentState.text.length > 10000) { // 10k+ characters
         await performanceOptimizer.forceOptimization(documentState);
         console.log('✅ Large document optimizations enabled');
       }
       
       // 9. Setup event listeners
       setupEventListeners();
       
       // 10. Start systems
       startAllSystems();
       
       // Ensure caret is visible and blinking on initial load (even with empty text)
       inputManager.focus();
       
       setEditorState(prev => ({
         ...prev,
         isInitialized: true,
         isLoading: false
       }));
       
       initializationInProgress.current = false; // Reset flag on success
       
     } catch (error) {
       console.error('[ClientFirstDocumentEditor] Initialization failed:', error);
       initializationInProgress.current = false; // Reset flag on error
       onError?.(error as Error);
       
       setEditorState(prev => ({
         ...prev,
         isLoading: false
       }));
     }
   };
 
   const setupEventListeners = () => {
     // Document change events (textChanged is handled in initialization - see line 699)
     // documentEngine.on('textChanged', handleDocumentChange); // 🎯 REMOVED: Consolidated into main listener
     documentEngine.on('caretMoved', handleCaretChange);
     documentEngine.on('selectionChanged', handleSelectionChange);
     
     // Collaboration events
     // Collaboration events now handled by CollaborationIntegrationBridge
     
     // Sync events
     syncManager.on('syncComplete', handleSyncComplete);
     syncManager.on('syncError', handleSyncError);
     syncManager.on('connectionChanged', handleConnectionChange);
     // Apply backend layout plan when available (Phase 1, Step 1.3: SyncManager → CanvasRenderer integration)
     syncManager.on('layout-plan-updated', ({ layoutPlan, performance, sequence }: any) => {
       try {
         // 🎯 RACE CONDITION FIX: Only apply layout plans in sequence order
         if (sequence <= lastAppliedSequence) {
           console.log('🔧 [RACE_FIX] Ignoring out-of-order layout plan:', {
             receivedSequence: sequence,
             lastAppliedSequence,
             planHash: layoutPlan?.planHash?.slice(0, 8)
           });
           return;
         }
         
         
         if (layoutPlan && layoutPlan.pages && layoutPlan.pages.length > 0) {
           setLastAppliedSequence(sequence);
           
           
           // 🔧 CRITICAL FIX: Update paperDimensions to match backend layout
           updatePaperDimensionsFromLayout(layoutPlan);
           
           // Render using inline renderer (CanvasRenderer commented out per user request)
           // Enhanced PaperCanvas handles layout plans via props
         const paperCanvasAPI = (canvasRef.current as any)?.paperCanvasAPI;
         if (paperCanvasAPI) {
           paperCanvasAPI.setBackendLayoutPlan(layoutPlan);
         }
         // Update state for prop passing - CRITICAL: Ensure state persists for hit testing
         setCurrentBackendLayoutPlan(layoutPlan);
         
           
         } else {
           console.warn('⚠️ [LAYOUT_INTEGRATION] Invalid layout plan received:', {
             hasLayoutPlan: !!layoutPlan,
             hasPages: !!layoutPlan?.pages,
             pageCount: layoutPlan?.pages?.length || 0
           });
         }
       } catch (e) {
         console.error('❌ [LAYOUT_INTEGRATION] Failed to apply backend layout plan:', e);
       }
     });
     
     // Handle layout plan errors (Phase 1, Step 1.3: Error handling)
     syncManager.on('layout-plan-error', ({ error }: any) => {
       console.error('❌ [LAYOUT_INTEGRATION] Layout plan error:', error);
       // Only reset layout on critical errors, not minor ones
       const isCriticalError = error?.status >= 500 || error?.code === 'NETWORK_ERROR' || error?.code === 'TIMEOUT';
       if (isCriticalError) {
         const paperCanvasAPI = (canvasRef.current as any)?.paperCanvasAPI;
         if (paperCanvasAPI) {
           paperCanvasAPI.setBackendLayoutPlan(null);
         }
         setCurrentBackendLayoutPlan(null);
       } else {
       }
     });
     
     // Legal analysis events
     window.addEventListener('edgedocs-legal-analysis', handleLegalAnalysis);
     window.addEventListener('edgedocs-compliance-status', handleComplianceStatus);
     
     // Performance events
     window.addEventListener('edgedocs-performance-warning', handlePerformanceWarning);
     
     console.log('✅ Event listeners setup complete');
   };
 
   const startAllSystems = async () => {
     // Gate first paint until required font is ready
     try {
       const requiredFont = editorState.currentFontRequirements?.requiredFonts?.[0] || 'Times New Roman';
       
      // Check if font is actually loaded
      if (document.fonts) {
        const fontFace = Array.from(document.fonts).find(f => f.family.includes('Times New Roman'));
        setEditorState(prev => ({
          ...prev,
          fontLoadingState: {
            ...prev.fontLoadingState,
            found: !!fontFace,
            status: fontFace?.status,
            family: fontFace?.family,
            totalFonts: document.fonts.size
          }
        }));
      }
       
       // Ensure CanvasRenderer uses the declared font
       try { 
         const paperCanvasAPI = (canvasRef.current as any)?.paperCanvasAPI;
         if (paperCanvasAPI) {
           paperCanvasAPI.setPreferredFontFamily(requiredFont);
         }
       } catch (error) {
         console.error('🔤 [FONT_DIAGNOSTIC] Failed to set font:', error);
       }
       // Wait for font if FontFaceSet is available
       if ((document as any).fonts && typeof (document as any).fonts.load === 'function') {
         try {
           await (document as any).fonts.load(`16px "${requiredFont}"`);
           
          // Double-check availability; if not, fall through to timeout gate
          const ok = (document as any).fonts.check(`16px "${requiredFont}"`);
          setEditorState(prev => ({
            ...prev,
            fontLoadingState: {
              ...prev.fontLoadingState,
              font: requiredFont,
              loaded: ok,
              totalFontsAfterLoad: document.fonts.size
            }
          }));
           
           if (!ok) {
             console.warn('🔤 [FONT_DIAGNOSTIC] Font not ready after load, waiting 250ms...');
             await new Promise(res => setTimeout(res, 250));
           }
         } catch (error) {
           console.error('🔤 [FONT_DIAGNOSTIC] Font loading failed:', error);
           // Soft wait if load throws in some environments
           await new Promise(res => setTimeout(res, 150));
         }
       } else {
         // Environments without FontFaceSet: short stabilization wait
         await new Promise(res => setTimeout(res, 150));
       }
     } catch {}
 
     // Render loop already started during initialization
     
    // Start input processing
    inputManager.startInputProcessing();
    
    // Connect formatting information from InputManager to SyncManager
    // Use EventEmitter's on method for formatting updates
    inputManager.on('formatting-updated', (formatting: any[]) => {
      syncManager.setDocumentFormatting(formatting);
    });
     
     // Start background sync
     syncManager.startBackgroundSync();
     
     // Collaboration automatically started by integration bridge
     
     // Start performance monitoring
     performanceOptimizer.startPerformanceMonitoring();
     
     console.log('✅ All systems started');
   };
 
   // ============================================================================
   // EVENT HANDLERS
   // ============================================================================
 
   // 🎯 REMOVED: handleDocumentChange - now using operationCompleted listener instead
 
   const handleCaretChange = useCallback((data: { position: number }) => {
     // Update caret rendering
     const paperCanvasAPI = (canvasRef.current as any)?.paperCanvasAPI;
     if (paperCanvasAPI) {
       paperCanvasAPI.updateCaret(data.position);
     }
   }, []);
 
   const handleSelectionChange = useCallback((data: { 
     start: number; 
     end: number; 
     text: string 
   }) => {
     // Update selection rendering
     const paperCanvasAPI = (canvasRef.current as any)?.paperCanvasAPI;
     if (paperCanvasAPI) {
       paperCanvasAPI.updateSelection(data.start, data.end);
     }
   }, []);
 
   const handleCollaboratorJoin = useCallback((collaborator: any) => {
     setEditorState(prev => ({
       ...prev,
       collaborators: [...prev.collaborators, collaborator]
     }));
     
     onCollaboratorJoin?.(collaborator);
     
   }, [onCollaboratorJoin]);
 
   const handleCollaboratorLeft = useCallback((collaborator: any) => {
     setEditorState(prev => ({
       ...prev,
       collaborators: prev.collaborators.filter(c => c.id !== collaborator.id)
     }));
     
   }, []);
 
   const handleRemoteOperation = useCallback((data: { 
     operation: Operation; 
     collaborator: any 
   }) => {
     // Apply remote operation
     documentEngine.applyRemoteOperation(data.operation, data.collaborator);
     
     // Update rendering
     // Enhanced PaperCanvas handles rendering internally
         const paperCanvasAPI = (canvasRef.current as any)?.paperCanvasAPI;
         if (paperCanvasAPI) {
           paperCanvasAPI.render();
         } // Use inline renderer instead
   }, []);
 
   const handleSyncComplete = useCallback((data: { 
     syncedOperations: Operation[]; 
     timestamp: Date 
   }) => {
     setEditorState(prev => ({
       ...prev,
       lastSyncTime: data.timestamp,
       isConnected: true
     }));
   }, []);
 
   const handleSyncError = useCallback((error: Error) => {
     console.error('[ClientFirstDocumentEditor] Sync error:', error);
     onError?.(error);
     
     setEditorState(prev => ({
       ...prev,
       isConnected: false
     }));
   }, [onError]);
 
   const handleConnectionChange = useCallback((data: { connected: boolean }) => {
     setEditorState(prev => ({
       ...prev,
       isConnected: data.connected
     }));
   }, []);
 
   const handleLegalAnalysis = useCallback((event: any) => {
     const { analysis } = event.detail;
     
     setEditorState(prev => ({
       ...prev,
       legalAnalysis: analysis
     }));
   }, []);
 
   const handleComplianceStatus = useCallback((event: any) => {
     const { violations } = event.detail;
     
     if (violations.length > 0) {
       console.warn(`[ClientFirstDocumentEditor] ${violations.length} compliance violations detected`);
     }
   }, []);
 
   const handlePerformanceWarning = useCallback((event: any) => {
     const { metric, value, threshold } = event.detail;
     console.warn(`[ClientFirstDocumentEditor] Performance warning: ${metric} = ${value} (threshold: ${threshold})`);
   }, []);
 
   // ============================================================================
   // PUBLIC METHODS
   // ============================================================================
 
   const exportDocument = useCallback(async (format: 'docx' | 'pdf', options: any = {}) => {
     try {
       const documentState = documentEngine.getState();
       const exportOptions = {
         format,
         includeMetadata: true,
         preserveFormatting: true,
         ...options
       };
       
       const result = await importExportManager.exportDocument(documentState, exportOptions);
       
       if (result.success) {
         // Download file
         const url = URL.createObjectURL(result.blob);
         const a = document.createElement('a');
         a.href = url;
         a.download = result.filename;
         a.click();
         URL.revokeObjectURL(url);
         
       } else {
         throw new Error(result.errors.join(', '));
       }
       
     } catch (error) {
       console.error('[ClientFirstDocumentEditor] Export failed:', error);
       onError?.(error as Error);
     }
   }, []);
 
   const importDocument = useCallback(async (file: File, options: any = {}) => {
     try {
       const importOptions = {
         preserveFormatting: true,
         extractImages: true,
         preserveComments: true,
         validateLegalContent: true,
         ...options
       };
       
       const result = await importExportManager.importDocument(file, importOptions);
       
       if (result.success) {
         // Load imported document
         await documentEngine.loadDocument(result.documentState);
         
         // Re-optimize for performance
         if (performanceOptimizer.shouldOptimizeDocument(result.documentState)) {
           await performanceOptimizer.optimizeDocument(result.documentState);
         }
         
         // Trigger re-render
         // Enhanced PaperCanvas handles rendering internally
         const paperCanvasAPI = (canvasRef.current as any)?.paperCanvasAPI;
         if (paperCanvasAPI) {
           paperCanvasAPI.render();
         } // Use inline renderer instead
         
         
         if (result.warnings.length > 0) {
           console.warn('[ClientFirstDocumentEditor] Import warnings:', result.warnings);
         }
       } else {
         throw new Error(result.errors.join(', '));
       }
       
     } catch (error) {
       console.error('[ClientFirstDocumentEditor] Import failed:', error);
       onError?.(error as Error);
     }
   }, []);
 
   const applyLegalTemplate = useCallback(async (templateId: string, variables: any = {}) => {
     try {
       const templateEngine = new DocumentTemplateEngine();
       
       const result = await templateEngine.generateDocument({
         templateId,
         variables,
         attorney,
         includeBoilerplate: true,
         applyFormatting: true,
         validateContent: true
       });
       
       if (result.success) {
         await documentEngine.loadDocument(result.documentState);
         // Enhanced PaperCanvas handles rendering internally
         const paperCanvasAPI = (canvasRef.current as any)?.paperCanvasAPI;
         if (paperCanvasAPI) {
           paperCanvasAPI.render();
         } // Use inline renderer instead
         
       } else {
         throw new Error(result.errors.join(', '));
       }
       
     } catch (error) {
       console.error('[ClientFirstDocumentEditor] Template application failed:', error);
       onError?.(error as Error);
     }
   }, [attorney]);
 
   const validateCompliance = useCallback(async () => {
     try {
       const documentState = documentEngine.getState();
       const violations = await legalCompliance.validateCompliance(
         documentState,
         jurisdiction,
         documentType
       );
       
       return {
         isCompliant: violations.length === 0,
         violations,
         totalViolations: violations.length,
         errors: violations.filter(v => v.severity === 'error').length,
         warnings: violations.filter(v => v.severity === 'warning').length
       };
       
     } catch (error) {
       console.error('[ClientFirstDocumentEditor] Compliance validation failed:', error);
       return {
         isCompliant: false,
         violations: [],
         totalViolations: 0,
         errors: 1,
         warnings: 0
       };
     }
   }, [jurisdiction, documentType]);
 
   // ============================================================================
   // DMS INTEGRATION ACTIONS
   // ============================================================================
 
   const saveDocument = useCallback(async () => {
     if (!saveRoutingManager || !documentEngine.getCurrentDocumentId()) {
       console.warn('[ClientFirstDocumentEditor] Save routing not initialized, cannot save');
       return;
     }
 
    try {
      setDmsState(prev => ({ ...prev, isSaving: true }));
      
      // 🔧 SYNC BEFORE SAVE: Sync PaperCanvas text to DocumentEngine before saving
      const paperCanvasAPI = (canvasRef.current as any)?.paperCanvasAPI;
      if (paperCanvasAPI && paperCanvasAPI.getDocumentState) {
        const currentCanvasState = paperCanvasAPI.getDocumentState();
        if (currentCanvasState?.text) {
          const updatedState = {
            ...documentEngine.getState(),
            text: currentCanvasState.text,
            modified: new Date()
          };
          await documentEngine.loadDocument(updatedState);
        }
      }
      
      // NEW ARCHITECTURE: Route save based on online/offline status AND checkout status
      const documentState = documentEngine.getState();
      const isCheckedOut = checkoutState?.isCheckedOut || false;
       
       const result = await saveRoutingManager.saveDocument(
         documentEngine.getCurrentDocumentId()!,
         documentState.text,
         undefined, // metadata
         isCheckedOut
       );
       
       if (result.success) {
         setDmsState(prev => ({ 
           ...prev, 
           isSaving: false, 
           lastSaveTime: new Date(),
           isDirty: false 
         }));
       } else {
         throw new Error(result.message || 'Save failed');
       }
     } catch (error) {
       console.error('[ClientFirstDocumentEditor] Failed to save document:', error);
       onError?.(error as Error);
       setDmsState(prev => ({ ...prev, isSaving: false }));
     }
   }, [lifecycleManager, documentEngine, onError]);
 
   const createNewVersion = useCallback(async (changeSummary?: string) => {
     if (!lifecycleManager || !documentEngine.getCurrentDocumentId()) {
       console.warn('[ClientFirstDocumentEditor] DMS not initialized, cannot create version');
       return;
     }
 
     try {
       setDmsState(prev => ({ ...prev, isSaving: true }));
       const result = await lifecycleManager.saveDocument(documentEngine.getCurrentDocumentId()!, {
         createNewVersion: true,
         changeSummary
       });
       
       if (result.success && result.versionId) {
         setDmsState(prev => ({ ...prev, isSaving: false }));
         return result.versionId;
       } else {
         throw new Error(result.message || 'Version creation failed');
       }
     } catch (error) {
       console.error('[ClientFirstDocumentEditor] Failed to create version:', error);
       onError?.(error as Error);
       setDmsState(prev => ({ ...prev, isSaving: false }));
       throw error;
     }
   }, [lifecycleManager, documentEngine, onError]);
 
   const createNewDocument = useCallback(async () => {
     if (!lifecycleManager || !clientId || !matterId || !documentTitle) {
       console.warn('[ClientFirstDocumentEditor] Missing required data for document creation');
       return;
     }
 
     try {
       setDmsState(prev => ({ ...prev, isCreatingDocument: true }));
       
       const currentText = documentEngine.getState().text;
       const result = await lifecycleManager.createNewDocument({
         title: documentTitle,
         client_id: clientId,
         matter_id: matterId,
         document_type: documentType,
         initialContent: currentText,
         attorney: attorney || {
           id: 'current-user',
           name: 'Current User',
           email: 'user@example.com',
           barNumber: '',
           firmName: '',
           role: 'partner',
           seniority: 0
         }
       });
       
       if (!result.success || !result.documentId) {
         throw new Error(result.message || 'Document creation failed');
       }
       
       const documentId = result.documentId;
       
       return documentId;
       
     } catch (error) {
       console.error('[ClientFirstDocumentEditor] Failed to create document:', error);
       onError?.(error as Error);
       setDmsState(prev => ({ ...prev, isCreatingDocument: false }));
       throw error;
     }
   }, [lifecycleManager, clientId, matterId, documentTitle, documentType, jurisdiction, documentEngine, onError]);
 
   // const toggleAutoSave = useCallback((enabled: boolean) => {
   //   if (!documentEngine.isDMSInitialized()) {
   //     console.warn('[ClientFirstDocumentEditor] DMS not initialized, cannot toggle auto-save');
   //     return;
   //   }
 
   //   if (enabled && autoSaveManager && documentEngine.getCurrentDocumentId()) {
   //     autoSaveManager.startAutoSave(documentEngine.getCurrentDocumentId()!, documentEngine);
   //   } else if (!enabled && autoSaveManager) {
   //     autoSaveManager.stopAutoSave();
   //   }
     
   //   setDmsState(prev => ({ ...prev, autoSaveEnabled: enabled }));
   // }, [autoSaveManager, documentEngine, autoSaveInterval]); // Commented out - using manual save only
 
   // Page Setup handlers
   const handlePageSetupOpen = useCallback(() => {
     setIsPageSetupOpen(true);
   }, []);
 
   const handlePageSetupClose = useCallback(() => {
     setIsPageSetupOpen(false);
   }, []);
 
   // Calculate paper dimensions from page setup settings
   const calculatePaperDimensionsFromPageSetup = useCallback((pageSetup: any) => {
     const dpi = pageSetup.dpi || 96;
     
     // Calculate page size in pixels
     let pageWidth = 816, pageHeight = 1056; // Letter default
     if (pageSetup.pageSize === 'A4') {
       pageWidth = Math.round(8.27 * dpi);
       pageHeight = Math.round(11.69 * dpi);
     } else if (pageSetup.pageSize === 'Legal') {
       pageWidth = Math.round(8.5 * dpi);
       pageHeight = Math.round(14 * dpi);
     } else if (typeof pageSetup.pageSize === 'object') {
       pageWidth = pageSetup.pageSize.widthPx;
       pageHeight = pageSetup.pageSize.heightPx;
     } else {
       // Letter
       pageWidth = Math.round(8.5 * dpi);
       pageHeight = Math.round(11 * dpi);
     }
     
     // Handle landscape orientation
     if (pageSetup.orientation === 'landscape') {
       [pageWidth, pageHeight] = [pageHeight, pageWidth];
     }
     
     // Calculate content area from margins
     const margins = pageSetup.margins || { top: 24, right: 24, bottom: 24, left: 24 };
     const contentX = margins.left;
     const contentY = margins.top;
     const contentWidth = pageWidth - margins.left - margins.right;
     const contentHeight = pageHeight - margins.top - margins.bottom;
     
     return {
       paperWidth: pageWidth,
       paperHeight: pageHeight,
       contentX,
       contentY,
       contentWidth,
       contentHeight,
       margins,
       dpi,
       orientation: pageSetup.orientation || 'portrait',
       pageSize: pageSetup.pageSize || 'Letter'
     };
   }, []);
 
   const handlePageSetupApply = useCallback(async (pageSetup: any) => {
     console.log('🔧 [PAGE_SETUP] Applying page setup:', pageSetup);
     
     // 🎯 IMMEDIATE APPLICATION: Update frontend immediately for responsive UI
     const newPaperDimensions = calculatePaperDimensionsFromPageSetup(pageSetup);
     setPaperDimensions(newPaperDimensions);
     console.log('✅ [PAGE_SETUP] Frontend immediately updated with new dimensions:', newPaperDimensions);
     
     if (syncManager) {
       try {
         // Send page setup to backend - backend layout response will be authoritative
         await syncManager.updatePageSetup(pageSetup);
         console.log('🔧 [PAGE_SETUP] Page setup successfully applied with new layout plan');
         
         // 💾 SAVE DPI TO DATABASE: Save page setup metadata via AST API
         if (documentId && versionId && pageSetup.dpi) {
           console.log('💾 [DPI_PERSISTENCE] Saving DPI metadata via AST API:', pageSetup.dpi);
           
           try {
             const token = localStorage.getItem('webelieve_access_token') || localStorage.getItem('access_token') || auth.token;
             
             // Get current AST
             const astResponse = await fetch(`/api/v1/dms/documents/${documentId}/versions/${versionId}/ast`, {
               headers: { 'Authorization': `Bearer ${token}` }
             });
             
             let currentAST = {};
             if (astResponse.ok) {
               const astData = await astResponse.json();
               currentAST = astData.ast || {};
               console.log('📄 [DPI_PERSISTENCE] Current AST loaded');
             } else {
               console.log('📄 [DPI_PERSISTENCE] No existing AST, creating new');
             }
             
             // Get current character formatting data
             const currentFormattingData = (canvasRef.current as any)?.formattingAPI?.getFormattingData();
             
             // Add page setup metadata to AST (preserve existing content structure and formatting)
             const updatedAST = {
               ...currentAST,
               metadata: {
                 ...(currentAST as any).metadata,
                 pageSetup: {
                   dpi: pageSetup.dpi,
                   pageSize: pageSetup.pageSize,
                   margins: pageSetup.margins,
                   orientation: pageSetup.orientation,
                   fontFamily: pageSetup.fontFamily,
                   fontSize: pageSetup.fontSize,
                   lastModified: new Date().toISOString()
                 },
                 // Preserve existing character formatting during page setup changes
                 characterFormatting: currentFormattingData || (currentAST as any).metadata?.characterFormatting
               }
             };
             
             // Save updated AST
             const saveResponse = await fetch(`/api/v1/dms/documents/${documentId}/versions/${versionId}/ast`, {
               method: 'POST',
               headers: {
                 'Content-Type': 'application/json',
                 'Authorization': `Bearer ${token}`
               },
               body: JSON.stringify({
                 ast: updatedAST,
                 ast_version: 'v1'
               })
             });
             
             if (saveResponse.ok) {
               console.log('✅ [DPI_PERSISTENCE] DPI metadata saved to AST successfully');
             } else {
               console.error('❌ [DPI_PERSISTENCE] Failed to save AST:', saveResponse.statusText);
             }
           } catch (error) {
             console.error('❌ [DPI_PERSISTENCE] Error saving DPI via AST API:', error);
           }
         }
       } catch (error) {
         console.error('🔧 [PAGE_SETUP] Failed to apply page setup:', error);
       }
     }
     
     setIsPageSetupOpen(false);
   }, [syncManager, calculatePaperDimensionsFromPageSetup, documentId, auth]);
 
   // Update document state for PaperCanvas when document engine changes
   useEffect(() => {
     if (documentEngine) {
       const updateDocumentState = () => {
         const newState = {
           text: documentEngine.getText(),
           caretPosition: documentEngine.getCaretPosition?.() || 0,
           selection: documentEngine.getSelection?.() || null,
           backendLayoutPlan: null
         };
        setCurrentDocumentState(newState);
        setEditorState(prev => ({
          ...prev,
          textLength: newState.text.length,
          textPreview: newState.text.slice(0, 50) || 'EMPTY'
        }));
       };
 
       // Initial update
       updateDocumentState();
 
       // Listen for document changes
       documentEngine.on('operationCompleted', updateDocumentState);
       documentEngine.on('textChanged', updateDocumentState);
       documentEngine.on('caretMoved', updateDocumentState);
 
       return () => {
         documentEngine.off('operationCompleted', updateDocumentState);
         documentEngine.off('textChanged', updateDocumentState);
         documentEngine.off('caretMoved', updateDocumentState);
       };
     }
   }, [documentEngine]);
 
   const updatePaperDimensionsFromLayout = useCallback((layoutPlan: any) => {
     if (layoutPlan?.pages?.[0]) {
       const page = layoutPlan.pages[0];
       const frame = page.frames?.[0];
       
       console.log('🔧 [ASYMMETRIC_MARGIN_FIX] Updating paperDimensions from backend layout:', {
         frameWidth: frame?.width,
         frameX: frame?.x,
         pageWidth: page.width,
         backendContentWidth: frame?.width,
         previousContentWidth: paperDimensions.contentWidth
       });
       
       setPaperDimensions({
         paperWidth: page.width || 816,
         paperHeight: page.height || 1056,
         contentX: frame?.x || 24,
         contentY: frame?.y || 24,
         contentWidth: frame?.width || 768,
         contentHeight: frame?.height || 1008,
         margins: layoutPlan.pageSetup?.margins || { top: 24, right: 24, bottom: 24, left: 24 },
         dpi: layoutPlan.pageSetup?.dpi || 96,
         orientation: layoutPlan.pageSetup?.orientation || 'portrait',
         pageSize: layoutPlan.pageSetup?.pageSize || 'Letter'
       });
     }
   }, []);
 
 
   const refreshLayoutPlan = useCallback(async () => {
     if (!syncManager) {
       console.warn('[ClientFirstDocumentEditor] Sync manager not available');
       return;
     }
 
     try {
       const layoutPlan = await syncManager.requestLayoutPlan(true);
       
       if (layoutPlan) { // Removed canvasRenderer check per user request
         // Enhanced PaperCanvas handles layout plans via props
         const paperCanvasAPI = (canvasRef.current as any)?.paperCanvasAPI;
         if (paperCanvasAPI) {
           paperCanvasAPI.setBackendLayoutPlan(layoutPlan);
         }
         // Update state for prop passing
         setCurrentBackendLayoutPlan(layoutPlan);
       }
     } catch (error) {
       console.error('[ClientFirstDocumentEditor] Failed to refresh layout plan:', error);
       onError?.(error as Error);
     }
   }, [syncManager, onError]); // Remove canvasRenderer dependency since it's commented out
 
   // ============================================================================
   // CLEANUP
   // ============================================================================
 
   const destroyEditor = async () => {
     try {
       
       // Stop all systems
       // canvasRenderer.stopRenderLoop(); // Commented out per user request
       inputManager.stopInputProcessing();
       syncManager.stopBackgroundSync();
       // collaborationBridge.disconnect(); // Commented out per user request
       
       // Clean up connectivity and save routing
       connectivityManager?.destroy();
       saveRoutingManager?.destroy();
       
       // Destroy components
       await documentEngine.destroy();
       // await canvasRenderer.destroy(); // Commented out per user request
       await inputManager.destroy();
       await syncManager.destroy();
       fontManager.destroy();
       // collaborationBridge.destroy(); // Commented out per user request
       await legalCompliance.destroy();
       await importExportManager.destroy();
       await performanceOptimizer.destroy();
       
       console.log('✅ Editor destroyed successfully');
       
     } catch (error) {
       console.error('[ClientFirstDocumentEditor] Destruction failed:', error);
     }
   };
 
   // ============================================================================
   // RENDER
   // ============================================================================
 
   if (editorState.isLoading) {
     return (
       <div className="flex items-center justify-center h-full">
         <div className="text-center">
           <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lex-accent mx-auto mb-2"></div>
           <div className="text-sm text-gray-600">Initializing EdgeDocs Client-First Editor...</div>
         </div>
       </div>
     );
   }
 
   /**
    * Initialize collaboration BEFORE document loading to prevent TS_DELTA_NO_TARGET errors
    */
   /**
    * Extract text content from backend layout plan for debugging
    */
   const extractTextFromBackendPlan = useCallback((plan: any): string => {
     if (!plan) return '';
     
     let extractedText = '';
     
     // Handle both direct lines format and pages/frames format
     if (plan?.pages?.[0]?.frames) {
       // New format: pages -> frames -> lines
       for (const frame of plan.pages[0].frames) {
         for (const line of frame.lines || []) {
           for (const run of line.runs || []) {
             if (run.text) {
               extractedText += run.text;
             }
           }
         }
       }
     } else if (plan?.lines) {
       // Direct format: lines
       for (const line of plan.lines) {
         for (const run of line.runs || []) {
           if (run.text) {
             extractedText += run.text;
           }
         }
       }
     }
     
     return extractedText;
   }, []);
 
  /**
   * Optimized backend re-layout request with smart change detection
   * Only triggers for shaping-critical changes identified by PaperCanvas
   */
  const optimizedBackendRelayout = useCallback(
    debounce(async (text: string, changeMetadata: ChangeMetadata) => {
      console.log('🔄 [OPTIMIZED_LAYOUT] Processing layout request:', {
        textLength: text.length,
        changeType: changeMetadata?.changeType,
        affectsKerning: changeMetadata?.affectsKerning,
        affectsLigatures: changeMetadata?.affectsLigatures,
        affectsMargins: changeMetadata?.affectsMargins
      });
      
      // 🔧 SMART SYNC: Only sync DocumentEngine when we're actually requesting layout
      // This eliminates unnecessary state synchronization for non-layout changes
      const updatedState = {
        ...documentEngine.getState(),
        text: text,
        modified: new Date()
      };
      await documentEngine.loadDocument(updatedState);
      
      try {
        // Request layout plan with change metadata for backend optimization
        const newLayoutPlan = await syncManager.requestLayoutPlan(true);
        if (newLayoutPlan) {
          console.log('✅ [OPTIMIZED_LAYOUT] Layout plan received successfully');
          
          // Debug what the backend processed
          const backendProcessedText = extractTextFromBackendPlan(newLayoutPlan);
          
          // Log performance metrics
          console.log('📊 [OPTIMIZED_LAYOUT] Performance metrics:', {
            textLength: text.length,
            changeType: changeMetadata?.changeType,
            shapingImpact: {
              kerning: changeMetadata?.affectsKerning,
              ligatures: changeMetadata?.affectsLigatures,
              margins: changeMetadata?.affectsMargins
            }
          });
        }
      } catch (error) {
        console.error('❌ [OPTIMIZED_LAYOUT] Failed:', error);
      }
    }, 100), // Keep responsive debounce for immediate feedback
    [syncManager, extractTextFromBackendPlan, documentEngine]
  );

  /**
   * Legacy debounced backend re-layout request (deprecated - use optimizedBackendRelayout)
   * @deprecated Use optimizedBackendRelayout instead for better performance
   */
  const debouncedBackendRelayout = useCallback(
    debounce(async (text: string) => {
      console.warn('⚠️ [DEPRECATED] debouncedBackendRelayout is deprecated. Use optimizedBackendRelayout instead.');
      
      // Create basic change metadata for legacy calls
      const legacyMetadata = {
        changeType: 'insert' as const,
        position: 0,
        characterCount: text.length,
        affectsKerning: true,
        affectsLigatures: true,
        affectsMargins: true,
        requiresLayoutUpdate: true,
        isWhitespace: false,
        isPunctuation: false,
        text: text
      };
      
      return optimizedBackendRelayout(text, legacyMetadata);
    }, 100),
    [optimizedBackendRelayout]
  );
 
   const initializeCollaborationFirst = async (documentId: string | undefined, dmsBaseUrl: string, authToken: string) => {
     if (!documentId || !websocketUrl) { // Remove collaborationBridge check since it's commented out
       console.log('🔄 [COLLABORATION_FIRST] Skipping - missing requirements');
       return;
     }
 
     try {
       console.log('🔄 [COLLABORATION_FIRST] Starting collaboration initialization...');
       
       // Auto-enable collaboration for EdgeDocs typography if not already enabled
       try {
         const checkResponse = await fetch(`${dmsBaseUrl}/api/v1/documents/${documentId}/collaboration`, {
           headers: {
             'Authorization': `Bearer ${authToken}`,
             'Content-Type': 'application/json',
           },
         });
         
         if (!checkResponse.ok && (checkResponse.status === 404 || checkResponse.status === 500)) {
           console.log('🚀 [COLLABORATION_FIRST] Auto-enabling collaboration...');
           
           const initResponse = await fetch(`${dmsBaseUrl}/api/v1/documents/${documentId}/collaboration`, {
             method: 'POST',
             headers: {
               'Authorization': `Bearer ${authToken}`,
               'Content-Type': 'application/json',
             },
             body: JSON.stringify({
               collaboration_mode: 'view_only',
               default_permissions: {
                 canEdit: false,
                 canComment: false,
                 canTrackChanges: false,
                 canAcceptReject: false,
                 canShare: false,
                 canManageUsers: false
               },
               allow_anonymous_comments: false,
               require_approval_for_changes: false,
               track_changes_enabled: false,
               comments_enabled: false,
               auto_enabled_for_typography: true
             })
           });
           
           if (initResponse.ok) {
             console.log('✅ [COLLABORATION_FIRST] Collaboration auto-enabled');
           } else {
             console.warn('⚠️ [COLLABORATION_FIRST] Failed to auto-enable:', initResponse.status);
           }
         }
       } catch (autoEnableError) {
         console.warn('⚠️ [COLLABORATION_FIRST] Auto-enable failed:', autoEnableError);
       }
       
       // Initialize collaboration bridge and WAIT for it to complete
       // await collaborationBridge.initialize({ // Commented out per user request
       //   documentId: documentId,
       //   versionId: versionId, // Pass version ID for version-specific collaboration
       //   websocketUrl,
       //   token: authToken,
       //   realmId: auth.realmId || 'default-realm',
       //   attorney: {
       //     id: attorney.id || 'unknown',
       //     name: attorney.name || 'Unknown Attorney',
       //     email: attorney.email || 'unknown@example.com'
       //   }
       // }); // Commented out per user request
       
       console.log('✅ [COLLABORATION_FIRST] Collaboration initialized successfully BEFORE document loading');
       
     } catch (error) {
       console.error('❌ [COLLABORATION_FIRST] Failed to initialize collaboration:', error);
       // Don't throw - allow document loading to continue even if collaboration fails
     }
   };
 
   return (
     <div 
       ref={containerRef}
       className="relative w-full h-full bg-white overflow-hidden"
       style={{ minHeight: '600px' }}
     >
       {/* 🎯 NEW: Document Toolbar */}
       <div className="absolute top-0 left-0 right-0 bg-white border-b border-gray-200 px-4 py-2 z-10">
         <div className="flex items-center justify-between">
           <div className="flex items-center space-x-4">
             <span className="text-sm font-medium text-gray-700">
               {documentTitle || 'Untitled Document'}
             </span>
             {editorState.isConnected ? (
               <span className="text-xs text-green-600">● Online</span>
             ) : (
               <span className="text-xs text-orange-600">● Offline Mode</span>
             )}
             
             {/* Text Formatting Toolbar */}
             {hasTextFormatting && (
               <div className="flex items-center space-x-1 pl-4 border-l border-gray-300">
                 <span className="text-xs text-gray-500">Formatted</span>
                 <div className="w-2 h-2 bg-lex-accent rounded-full" title="Document contains text formatting"></div>
               </div>
             )}
           </div>
           
           {/* Document Action Buttons */}
           <div className="flex items-center space-x-2">
             {/* Save Button */}
             <button
               onClick={handleSave}
               disabled={dmsState.isSaving || (!dmsState.isDirty && documentEngine.getText().length === 0)}
               className={`px-3 py-1 text-sm rounded transition-colors flex items-center gap-1 ${
                 dmsState.isSaving || (!dmsState.isDirty && documentEngine.getText().length === 0)
                   ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                   : 'bg-lex-accent text-lex-accent-contrast hover:bg-lex-accent-hover'
               }`}
               title={dmsState.isSaving ? 'Saving...' : (!dmsState.isDirty && documentEngine.getText().length === 0) ? 'No content to save' : 'Save document to current version'}
             >
               <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
               </svg>
               {dmsState.isSaving ? 'Saving...' : 'Save'}
             </button>
 
             {/* New Version Button */}
             <button
               onClick={handleNewVersion}
               disabled={dmsState.isCreatingVersion || !dmsState.isInitialized}
               className={`px-3 py-1 text-sm rounded transition-colors flex items-center gap-1 ${
                 dmsState.isCreatingVersion || !dmsState.isInitialized
                   ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                   : 'bg-green-600 text-white hover:bg-green-700'
               }`}
               title={dmsState.isCreatingVersion ? 'Creating version...' : 'Create new version'}
             >
               <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
               </svg>
               {dmsState.isCreatingVersion ? 'Creating...' : 'New Version'}
             </button>
 
             {/* Page Setup Button */}
             <button
               onClick={handlePageSetupOpen}
               className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors flex items-center gap-1"
               title="Configure page layout and typography"
             >
               <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
               </svg>
               Page Setup
             </button>
 
             {/* Performance Metrics Button - REMOVED: Now in Analytics Dashboard */}
             {/* Performance metrics moved to DMS Dashboard → Analytics Tab */}
 
             {/* Version History / Change Tracking Button */}
             <DocumentChangeButton
               documentId={documentId || ''}
               documentContent={documentEngine.getText()}
               documentTitle={documentTitle || 'Untitled Document'}
               variant="button"
               size="sm"
               className="bg-purple-600 text-white hover:bg-purple-700"
             />
 
             {/* CheckIn Button - only show when document is checked out by current user */}
             {false && ( // TODO: Replace with actual checkout status check
               <button
                 onClick={() => {
                   // TODO: Implement checkin functionality
                   console.log('CheckIn button clicked');
                 }}
                 className="px-3 py-1 text-sm bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors flex items-center gap-1"
                 title="Check in document and save changes"
               >
                 <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
                 </svg>
                 Check In
               </button>
             )}
           </div>
         </div>
       </div>
 
       {/* Main Canvas with PaperCanvas */}
       <div 
         className="absolute"
         style={{ 
           top: '48px', // Account for toolbar height
           left: '0',
           right: '0',
           bottom: '32px', // Account for status bar height
           outline: 'none'
         }}
       >
         <PaperCanvas
           paperDimensions={{
             paperWidth: currentBackendLayoutPlan?.pageSizePx?.width || (currentBackendLayoutPlan as any)?.pageSetup?.widthPx || paperDimensions.paperWidth,
             paperHeight: currentBackendLayoutPlan?.pageSizePx?.height || (currentBackendLayoutPlan as any)?.pageSetup?.heightPx || paperDimensions.paperHeight,
             contentX: currentBackendLayoutPlan?.contentRectPx?.x || (currentBackendLayoutPlan as any)?.pages?.[0]?.frames?.[0]?.x || paperDimensions.contentX,
             contentY: currentBackendLayoutPlan?.contentRectPx?.y || (currentBackendLayoutPlan as any)?.pages?.[0]?.frames?.[0]?.y || paperDimensions.contentY,
             contentWidth: currentBackendLayoutPlan?.contentRectPx?.width || (currentBackendLayoutPlan as any)?.pages?.[0]?.frames?.[0]?.width || paperDimensions.contentWidth,
             contentHeight: currentBackendLayoutPlan?.contentRectPx?.height || (currentBackendLayoutPlan as any)?.pages?.[0]?.frames?.[0]?.height || paperDimensions.contentHeight,
             margins: (currentBackendLayoutPlan as any)?.pageSetup?.margins || paperDimensions.margins,
             dpi: currentBackendLayoutPlan?.dpi || (currentBackendLayoutPlan as any)?.pageSetup?.dpi || paperDimensions.dpi,
             orientation: (currentBackendLayoutPlan as any)?.pageSetup?.orientation || paperDimensions.orientation,
             pageSize: (currentBackendLayoutPlan as any)?.pageSetup?.pageSize || 'Letter'
           }}
           fontColor={(syncManager as any)?.currentFontSettings?.fontColor || '#000000'}
           documentState={currentDocumentState}
           backendLayoutPlan={currentBackendLayoutPlan}
           caretSystem={inputManager.getCaretSystem()}
           onCanvasReady={(canvas, ctx) => {
             canvasRef.current = canvas;
             // Enhanced PaperCanvas now handles all rendering internally
             
             // Set focus and start caret blinking
             const paperCanvasAPI = (canvas as any)?.paperCanvasAPI;
             if (paperCanvasAPI) {
               paperCanvasAPI.setFocus(true);
             }
           }}
           onCaretChange={(position) => {
             // Set click-only flag to prevent state sync during click operations
             isClickOnlyOperation.current = true;
             
             // Clear the flag after a short delay to allow typing to trigger sync
             setTimeout(() => {
               isClickOnlyOperation.current = false;
             }, 100);
           }}
           onDocumentChange={async (newText, changeMetadata) => {
             console.log('🔄 [SMART_CHANGE] ClientFirstDocumentEditor received change:', {
               textLength: newText.length,
               hasMetadata: !!changeMetadata,
               requiresLayoutUpdate: changeMetadata?.requiresLayoutUpdate,
               affectsKerning: changeMetadata?.affectsKerning,
               affectsLigatures: changeMetadata?.affectsLigatures,
               affectsMargins: changeMetadata?.affectsMargins
             });
             
            // 🔧 SMART CHANGE DETECTION: Only trigger layout for shaping-critical changes
            // PaperCanvas now analyzes each change and only sends layout-critical changes here
            // This dramatically reduces backend layout requests while maintaining HarfBuzz accuracy
            
            if (changeMetadata?.requiresLayoutUpdate) {
              console.log('🔄 [SMART_CHANGE] Triggering optimized layout update');
              optimizedBackendRelayout(newText, changeMetadata);
            } else {
              console.log('⚡ [SMART_CHANGE] Skipping layout update - no shaping impact detected');
            }
           }}
           onSelectionChange={(start, end) => {
             console.log('🎯 [SELECTION] PaperCanvas selection changed:', { start, end });
             setSelectionStart(start);
             setSelectionEnd(end);
           }}
           preferredFontFamily="Times New Roman"
           showMarginGuides={true}
           enableCollaboration={false}
           className="w-full h-full"
         />
       </div>
       
       {/* Status Bar */}
       <div className="absolute bottom-0 left-0 right-0 bg-gray-100 border-t border-gray-200 px-4 py-2 text-xs text-gray-600 flex items-center justify-between">
         <div className="flex items-center space-x-4">
           <span>
             {editorState.isConnected ? (
               <span className="text-green-600">● Connected</span>
             ) : (
               <span className="text-red-600">● Offline</span>
             )}
           </span>
           
           {/* Typography Performance Metrics */}
           {editorState.performanceMetrics && (
             <div className="flex items-center space-x-3 text-xs">
               <span className={`px-2 py-1 rounded ${
                 editorState.performanceMetrics.typographyMetrics.isOptimal 
                   ? 'bg-green-100 text-green-700' 
                   : 'bg-yellow-100 text-yellow-700'
               }`}>
                 Typography: {editorState.performanceMetrics.typographyMetrics.isOptimal ? 'Optimal' : 'Basic'}
               </span>
               
               <span className="text-gray-500">
                 Render: {editorState.performanceMetrics.typographyMetrics.averageRenderingTime.toFixed(1)}ms
               </span>
               
               <span className="text-gray-500">
                 FPS: {editorState.performanceMetrics.frameRate.toFixed(0)}
               </span>
               
               {editorState.performanceMetrics.typographyMetrics.fallbackUsageRate > 0 && (
                 <span className="px-2 py-1 rounded bg-orange-100 text-orange-700">
                   Fallback: {(editorState.performanceMetrics.typographyMetrics.fallbackUsageRate * 100).toFixed(0)}%
                 </span>
               )}
               
               {/* HarfBuzz/MSDF Readiness Indicator */}
               {!editorState.performanceMetrics.systemCapabilities.hasHarfBuzz && 
                editorState.performanceMetrics.typographyMetrics.totalOperations > 50 && (
                 <span className="px-2 py-1 rounded bg-lex-accent-soft text-lex-accent">
                   Ready for HarfBuzz
                 </span>
               )}
             </div>
           )}
           
           {/* Font Loading Status */}
           <div className="flex items-center space-x-3 text-xs">
             {editorState.fontLoadingState.loadingFonts.size > 0 && (
               <span className="px-2 py-1 rounded bg-lex-accent-soft text-lex-accent animate-pulse">
                 Loading {editorState.fontLoadingState.loadingFonts.size} font(s)
               </span>
             )}
             
             {editorState.fontLoadingState.loadedFonts.size > 0 && (
               <span className="px-2 py-1 rounded bg-green-100 text-green-700">
                 {editorState.fontLoadingState.loadedFonts.size} font(s) loaded
               </span>
             )}
             
             {editorState.fontLoadingState.failedFonts.size > 0 && (
               <span className="px-2 py-1 rounded bg-red-100 text-red-700">
                 {editorState.fontLoadingState.failedFonts.size} font(s) failed
               </span>
             )}
             
             {editorState.fontLoadingState.harfbuzzReady && (
               <span className="px-2 py-1 rounded bg-purple-100 text-purple-700">
                 HarfBuzz Ready
               </span>
             )}
             
             {editorState.currentFontRequirements && (
               <span className="text-gray-500">
                 {editorState.currentFontRequirements.jurisdiction.toUpperCase()} • 
                 {editorState.currentFontRequirements.requiredFonts[0]} • 
                 {editorState.currentFontRequirements.fontSize.default}pt
               </span>
             )}
           </div>
           
           {editorState.collaborators.length > 0 && (
             <span>{editorState.collaborators.length} collaborator(s)</span>
           )}
           
           {editorState.lastSyncTime && (
             <span>Last sync: {editorState.lastSyncTime.toLocaleTimeString()}</span>
           )}
         </div>
         
         <div className="flex items-center space-x-4">
           {editorState.performanceMetrics && (
             <span>
               {editorState.performanceMetrics.frameRate?.toFixed(0)}fps
             </span>
           )}
           
           <span>{documentEngine.getState().text.length.toLocaleString()} chars</span>
           
           <span>v{documentEngine.getState().version}</span>
         </div>
       </div>
       
       {/* Remote cursors now rendered directly in CanvasRenderer */}
       
       {/* Legal Compliance Indicator */}
       {editorState.legalAnalysis && (
         <div className="absolute top-4 right-4">
           <div className={`px-3 py-1 rounded-full text-xs font-medium ${
             editorState.legalAnalysis.completeness > 0.8
               ? 'bg-green-100 text-green-800'
               : editorState.legalAnalysis.completeness > 0.5
               ? 'bg-yellow-100 text-yellow-800'
               : 'bg-red-100 text-red-800'
           }`}>
             {(editorState.legalAnalysis.completeness * 100).toFixed(0)}% Complete
           </div>
         </div>
       )}
 
       {/* Version Creation Modal */}
       <DocumentVersioningModal
         isOpen={showVersionModal}
         onClose={() => {
           setShowVersionModal(false);
           setPendingVersionContent(null);
         }}
         documentId={documentId || ''}
         currentContent={pendingVersionContent || ''}
         onVersionDecision={handleVersionDecision}
         documentTitle={documentTitle || 'Untitled Document'}
       />
 
       {/* Text Formatting Integration */}
       {(() => {
         console.log('🎨 [TEXT_FORMATTING] Rendering TextFormattingIntegration', {
           hasDocumentEngine: !!documentEngine,
           hasCanvasRef: !!canvasRef,
           canvasRefCurrent: !!canvasRef?.current
         });
         return (
          <TextFormattingIntegration
            documentEngine={documentEngine}
            canvasRef={canvasRef}
            currentBackendLayoutPlan={currentBackendLayoutPlan}
            paperDimensions={paperDimensions}
            onPageSetup={handlePageSetupOpen}
            onFormatChange={setHasTextFormatting}
            onSelectionChange={(start, end) => {
              console.log('🎯 [SELECTION] TextFormattingIntegration selection change:', { start, end });
              setSelectionStart(start);
              setSelectionEnd(end);
            }}
            selectionStart={selectionStart}
            selectionEnd={selectionEnd}
          />
         );
       })()}
 
       {/* Page Setup Dialog */}
       <PageSetupDialog
         isOpen={isPageSetupOpen}
         onClose={handlePageSetupClose}
         onApply={handlePageSetupApply}
         currentSetup={syncManager?.getCurrentPageSetup() || {}}
       />
 
       {/* Performance Metrics Panel - MOVED TO ANALYTICS DASHBOARD */}
       {/* 
         Performance metrics are now available in:
         DMS Dashboard → Analytics Tab → System Performance Panel
         
         This provides realm-wide visibility instead of document-specific,
         which is more appropriate since metrics are shared across all documents.
       */}
     </div>
   );
 };
 
 export default ClientFirstDocumentEditor;